/**
 * LogicTree - Hierarchical visualization of AI research flow
 *
 * Displays the research process as a "Living Blueprint" that grows:
 * - User → Planner → Tasks → Agents → Fact-Checker → Debate → Verdict
 * - Live streaming text shows real-time agent activity
 * - SVG Bezier curves for premium connector lines
 * - Auto-scroll to keep active nodes in view
 * - Debate branch pulses red/amber during conflict
 */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Tooltip } from '@/components/ui/Tooltip';
import { useAuth } from '@/contexts/AuthContext';
import { useIsMobile, useIsTablet } from '@/hooks/useMediaQuery';
import {
  User,
  Brain,
  Zap,
  Users,
  Scale,
  MessageSquare,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  X,
  Clock,
  Globe,
  FileText,
  ArrowRight,
  ShieldCheck,
  BookOpen,
  Newspaper,
  Lock,
} from 'lucide-react';
import type { ResearchSession, ActiveAgent, DebateState, AgentOutputData, TaskAgentStatus, FactCheckProgress, SourceInfo } from '@/hooks/useResearchSession';
import type { TaskBreakdown, TaskExecutionMode } from '@/types';
import { getProviderFromModel as detectProvider, getProviderLogo, getProviderColor as getProvColor } from '@/utils/providerUtils';
import { INTENSITY_CONFIGS } from '@/types';

import { MarkdownContent } from './MarkdownContent';
import { ExportMenu } from './ExportMenu';
import { handleExport } from '@/utils/exportUtils';
import type { ExportFormat, ResearchMode } from '@/types/researchModes';
import { RESEARCH_MODE_CONFIGS, getAgentRoleForModel } from '@/types/researchModeConfigs';
import { Microscope } from 'lucide-react';
import { CapabilityBadge } from './CapabilityBadge';

/** Execution mode icon component */
function ExecutionModeIcon({ mode }: { mode: TaskExecutionMode }) {
  if (mode === 'parallel') {
    return (
      <Tooltip content="These tasks run simultaneously to speed up research" position="top">
        <span>
          <Zap className="w-3 h-3 text-amber-500" />
        </span>
      </Tooltip>
    );
  }
  return (
    <Tooltip content="This task waits for previous tasks to finish because it depends on their results" position="top">
      <span>
        <ArrowRight className="w-3 h-3 text-blue-500" />
      </span>
    </Tooltip>
  );
}

interface LogicTreeProps {
  session: ResearchSession;
  className?: string;
  onAgentClick?: (agentOutput: AgentOutputData) => void;
  onNewResearch?: () => void;
  /** When true, hides export menus and action buttons (used in showcase/landing page) */
  hideActions?: boolean;
  /** When true, collapses to show only executive summary */
  isCollapsed?: boolean;
  /** Callback to toggle collapsed state */
  onToggleCollapse?: () => void;
}

/** Streaming text state for live output */
interface StreamingText {
  nodeId: string;
  fullText: string;
  displayedText: string;
  isComplete: boolean;
}

export function LogicTree({ session, className, onAgentClick, onNewResearch, hideActions, isCollapsed = false, onToggleCollapse }: LogicTreeProps) {
  const { query, phase, tasks, currentTaskIndex, agents, debate, debatesPerTask, reportData, agentOutputs, taskAgentStatuses, factCheckProgress, error, effectiveMode, requestedMode } = session;
  // Use effectiveMode if available, fallback to requestedMode, then 'auto'
  const researchMode: ResearchMode = effectiveMode || requestedMode || 'auto';
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollContentRef = useRef<HTMLDivElement>(null);
  const [streamingTexts, setStreamingTexts] = useState<Map<string, StreamingText>>(new Map());
  const [expandedTasks, setExpandedTasks] = useState<Set<number>>(new Set());
  const [selectedAgent, setSelectedAgent] = useState<AgentOutputData | null>(null);
  const [revealedTasks, setRevealedTasks] = useState<number>(0);
  const [showAgentModal, setShowAgentModal] = useState(false);

  // Auto-scroll state - respects user scroll intent
  const [userHasScrolled, setUserHasScrolled] = useState(false);
  const lastScrollTopRef = useRef(0);
  const userScrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Responsive hooks
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const isSmallScreen = isMobile || isTablet;

  // Auth and navigation for follow-up questions
  const { user } = useAuth();
  const navigate = useNavigate();

  // Auto-expand task when its debate starts (ensures inline DebateBranch is visible)
  useEffect(() => {
    if (debate?.isActive && debate.taskIndex >= 0) {
      setExpandedTasks(prev => {
        const newSet = new Set(prev);
        newSet.add(debate.taskIndex);
        return newSet;
      });
    }
  }, [debate?.isActive, debate?.taskIndex]);

  // Animate task reveal when tasks are created
  useEffect(() => {
    if (tasks.length > 0 && revealedTasks < tasks.length) {
      const timer = setTimeout(() => {
        setRevealedTasks(prev => Math.min(prev + 1, tasks.length));
      }, 400); // Stagger each task by 400ms
      return () => clearTimeout(timer);
    }
  }, [tasks.length, revealedTasks]);

  // Reset revealed tasks when query changes
  useEffect(() => {
    if (phase === 'idle' || phase === 'submitting') {
      setRevealedTasks(0);
      setExpandedTasks(new Set());
    }
  }, [phase]);

  // Auto-expand ALL revealed tasks by default (show prompt generator output)
  useEffect(() => {
    if (revealedTasks > 0) {
      // Expand all revealed tasks
      setExpandedTasks(prev => {
        const newSet = new Set(prev);
        for (let i = 0; i < revealedTasks; i++) {
          newSet.add(i);
        }
        return newSet;
      });
      // Note: Scrolling is handled by the unified auto-scroll effect below
    }
  }, [revealedTasks]);

  // Build parallel group map for expansion logic
  const parallelGroupMapForExpansion = useMemo(() => {
    const map = new Map<number, number[]>();
    let currentGroup: { mode: string; indices: number[] } | null = null;

    tasks.forEach((task, idx) => {
      const mode = task.execution_mode || 'parallel';
      if (!currentGroup || currentGroup.mode !== mode) {
        currentGroup = { mode, indices: [] };
      }
      currentGroup.indices.push(idx);
      // Only create parallel groups for parallel tasks with multiple tasks
      if (mode === 'parallel' && currentGroup.indices.length > 1) {
        currentGroup.indices.forEach(i => map.set(i, [...currentGroup!.indices]));
      }
    });
    return map;
  }, [tasks]);

  // Also expand current task (and all parallel group members) when it changes
  useEffect(() => {
    if (currentTaskIndex >= 0 && phase !== 'concluded') {
      const parallelGroup = parallelGroupMapForExpansion.get(currentTaskIndex);
      if (parallelGroup) {
        // Expand all tasks in the parallel group
        setExpandedTasks(prev => {
          const newSet = new Set(prev);
          parallelGroup.forEach(idx => newSet.add(idx));
          return newSet;
        });
      } else {
        // Just expand the current task
        setExpandedTasks(prev => new Set(prev).add(currentTaskIndex));
      }
    }
  }, [currentTaskIndex, phase, parallelGroupMapForExpansion]);

  // Handle agent click - show modal
  const handleAgentClick = useCallback((output: AgentOutputData) => {
    setSelectedAgent(output);
    setShowAgentModal(true);
    onAgentClick?.(output);
  }, [onAgentClick]);

  // Helper: Check if user is near bottom of scroll container (within threshold)
  const isNearBottom = useCallback((container: HTMLElement, threshold = 150) => {
    const { scrollTop, scrollHeight, clientHeight } = container;
    return scrollHeight - scrollTop - clientHeight < threshold;
  }, []);

  // Track user scroll intent - detect when user scrolls away from bottom
  useEffect(() => {
    const scrollContainer = scrollContentRef.current;
    if (!scrollContainer) return;

    let scrollTimeout: ReturnType<typeof setTimeout> | null = null;

    const handleScroll = () => {
      // Clear any pending timeout
      if (scrollTimeout) clearTimeout(scrollTimeout);

      // Check if user scrolled away from bottom
      if (!isNearBottom(scrollContainer, 100)) {
        setUserHasScrolled(true);
        lastScrollTopRef.current = scrollContainer.scrollTop;

        // Auto-resume after 8 seconds of no scrolling (user likely done reading)
        scrollTimeout = setTimeout(() => {
          setUserHasScrolled(false);
        }, 8000);
        userScrollTimeoutRef.current = scrollTimeout;
      } else {
        // User scrolled back to bottom - resume auto-scroll
        setUserHasScrolled(false);
      }
    };

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
      if (scrollTimeout) clearTimeout(scrollTimeout);
    };
  }, [isNearBottom]);

  // Reset user scroll state when research starts fresh
  useEffect(() => {
    if (phase === 'planning' || phase === 'submitting') {
      setUserHasScrolled(false);
      if (userScrollTimeoutRef.current) {
        clearTimeout(userScrollTimeoutRef.current);
        userScrollTimeoutRef.current = null;
      }
    }
  }, [phase]);

  // Unified auto-scroll - respects user scroll intent
  // Only scrolls on significant events, not every state change
  const prevTaskIndexRef = useRef(currentTaskIndex);
  const prevRevealedTasksRef = useRef(revealedTasks);
  const prevPhaseRef = useRef(phase);

  useEffect(() => {
    if (!scrollContentRef.current || phase === 'idle' || isCollapsed) return;

    // Skip if user has manually scrolled away
    if (userHasScrolled) return;

    const scrollContainer = scrollContentRef.current;

    // Determine if this is a significant event worth scrolling for
    const taskIndexChanged = currentTaskIndex !== prevTaskIndexRef.current;
    const newTasksRevealed = revealedTasks > prevRevealedTasksRef.current;
    const phaseChanged = phase !== prevPhaseRef.current;
    const isSignificantPhase = phase === 'concluded' || phase === 'deliberating';

    // Update refs
    prevTaskIndexRef.current = currentTaskIndex;
    prevRevealedTasksRef.current = revealedTasks;
    prevPhaseRef.current = phase;

    // Only auto-scroll on significant events
    const shouldScroll = taskIndexChanged || newTasksRevealed || (phaseChanged && isSignificantPhase);
    if (!shouldScroll) return;

    // Delay to allow DOM to update
    const timer = setTimeout(() => {
      // Double-check user hasn't scrolled during the delay
      if (!isNearBottom(scrollContainer, 200)) return;

      scrollContainer.scrollTo({
        top: scrollContainer.scrollHeight,
        behavior: 'smooth'
      });
    }, 200);

    return () => clearTimeout(timer);
  }, [phase, currentTaskIndex, revealedTasks, isCollapsed, userHasScrolled, isNearBottom]);

  // Generate streaming text effect for agents using actual output when available
  useEffect(() => {
    // Show streaming text for each active agent
    agents.forEach((agent, agentIndex) => {
      const nodeId = `agent-${currentTaskIndex}-${agent.id}`;
      const outputKey = `${currentTaskIndex}-${agentIndex}`;
      const agentOutput = agentOutputs?.get(outputKey);

      if (agent.status === 'researching' || agent.status === 'thinking') {
        // Agent is still working - show status text
        const statusText = agent.status === 'researching'
          ? `Searching web sources...`
          : `Analyzing findings...`;
        addStreamingText(nodeId, statusText);
      } else if (agentOutput?.outputPreview) {
        // Agent completed - show output preview (truncated to 100 chars)
        const preview = agentOutput.outputPreview.slice(0, 100) + (agentOutput.outputPreview.length > 100 ? '...' : '');
        addStreamingText(nodeId, preview);
      }
    });
  }, [agents, currentTaskIndex, phase, agentOutputs]);

  // Add streaming text with typewriter effect
  const addStreamingText = (nodeId: string, text: string) => {
    setStreamingTexts(prev => {
      const newMap = new Map(prev);
      newMap.set(nodeId, {
        nodeId,
        fullText: text,
        displayedText: '',
        isComplete: false,
      });
      return newMap;
    });

    // Animate the text
    let charIndex = 0;
    const interval = setInterval(() => {
      charIndex++;
      setStreamingTexts(prev => {
        const newMap = new Map(prev);
        const entry = newMap.get(nodeId);
        if (entry && charIndex <= text.length) {
          newMap.set(nodeId, {
            ...entry,
            displayedText: text.slice(0, charIndex),
            isComplete: charIndex >= text.length,
          });
        }
        return newMap;
      });
      if (charIndex >= text.length) clearInterval(interval);
    }, 30);

    return () => clearInterval(interval);
  };

  const toggleTask = (taskIndex: number) => {
    setExpandedTasks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(taskIndex)) newSet.delete(taskIndex);
      else newSet.add(taskIndex);
      return newSet;
    });
  };

  const showPlanner = phase !== 'idle';
  const showTasks = tasks.length > 0;
  const showAggregator = phase === 'concluded';

  // Check if all fact-checks are complete before showing the aggregator loading state
  // This prevents showing "Synthesizing Final Verdict" while fact-checkers are still spinning
  const allFactChecksComplete = tasks.length > 0 && tasks.every((_, idx) => {
    const progress = factCheckProgress?.get(idx);
    return progress?.isComplete === true;
  });
  const showAggregatorLoading = session.isFinalAggregating && phase === 'deliberating' && allFactChecksComplete;

  // Handle follow-up questions navigation for free users
  const handleFollowUp = useCallback(() => {
    if (reportData?.verdict) {
      // Navigate to dashboard with verdict as initial context
      navigate('/dashboard', {
        state: {
          initialVerdict: reportData.verdict,
          originalQuery: query,
          sources: reportData.sources || [],
        }
      });
    }
  }, [navigate, reportData?.verdict, reportData?.sources, query]);

  // Only show follow-up button for non-authenticated users (free users)
  const showFollowUpButton = !user && phase === 'concluded' && reportData?.verdict;

  // When collapsed, only show the executive summary
  if (isCollapsed && showAggregator && reportData?.verdict) {
    return (
      <div
        ref={containerRef}
        className={cn(
          'relative w-full max-w-none bg-surface border border-border-subtle overflow-hidden shadow-2xl',
          // Responsive border radius
          isSmallScreen ? 'rounded-xl' : 'rounded-2xl',
          className
        )}
      >
        {/* Collapsed Header with toggle */}
        <div className={cn(
          "border-b border-border-subtle bg-bar-bg/50 flex-shrink-0",
          isSmallScreen ? "px-3 py-3" : "px-6 py-4"
        )}>
          <div className="flex items-center justify-between gap-2">
            <div className={cn(
              "flex items-center gap-1.5 sm:gap-2 font-mono text-text-muted uppercase tracking-wider",
              isSmallScreen ? "text-xs" : "text-sm"
            )}>
              <Scale className={cn("text-verdict-primary", isSmallScreen ? "w-3.5 h-3.5" : "w-4 h-4")} />
              {/* Show blueprint name in collapsed view */}
              {(() => {
                const mode = session.effectiveMode || session.requestedMode || 'auto';
                const modeConfig = RESEARCH_MODE_CONFIGS[mode as ResearchMode];
                const blueprintName = modeConfig?.name || (mode === 'auto' ? 'General Research' : mode);
                return <span className="hidden xs:inline">{blueprintName}</span>;
              })()}
              <span className="xs:hidden">Research</span>
              <span className="hidden xs:inline">Complete</span>
              <span className={cn(
                "px-1.5 sm:px-2 py-0.5 bg-verdict-primary/20 rounded text-verdict-primary",
                isSmallScreen ? "text-[11px] ml-1" : "text-xs ml-2"
              )}>
                {tasks.length} Tasks
              </span>
              {/* Intensity badge in collapsed view */}
              {session.effectiveIntensity && (
                <span className={cn(
                  "px-1.5 py-0.5 rounded bg-surface-light/60 border border-border/50 text-text-secondary",
                  isSmallScreen ? "text-[10px] ml-1" : "text-xs ml-2"
                )}>
                  {INTENSITY_CONFIGS[session.effectiveIntensity]?.label}
                </span>
              )}
            </div>
            {onToggleCollapse && (
              <button
                onClick={onToggleCollapse}
                className={cn(
                  "flex items-center gap-1 text-text-secondary hover:text-text-primary hover:bg-surface-light rounded-lg transition-colors",
                  isSmallScreen ? "px-2 py-1 text-xs" : "px-3 py-1.5 text-sm"
                )}
              >
                <ChevronDown className={cn(isSmallScreen ? "w-3.5 h-3.5" : "w-4 h-4")} />
                <span className="hidden sm:inline">View</span> Details
              </button>
            )}
          </div>
        </div>

        {/* Collapsed Content - Only Executive Summary */}
        <div className={cn(isSmallScreen ? "p-3" : "p-4")}>
          <CollapsedAggregatorNode
            verdict={reportData.verdict}
            confidenceScore={reportData.confidenceScore}
            isSmallScreen={isSmallScreen}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative w-full max-w-none bg-surface border border-border-subtle overflow-x-hidden shadow-2xl',
        // Responsive border radius
        isSmallScreen ? 'rounded-xl' : 'rounded-2xl',
        'flex flex-col',
        className
      )}
    >
      {/* Header */}
      <div className={cn(
        "border-b border-border-subtle bg-bar-bg/50 flex-shrink-0",
        isSmallScreen ? "px-3 py-3" : "px-6 py-4"
      )}>
        <div className="flex items-center justify-between gap-2">
          <div className={cn(
            "flex items-center gap-1.5 sm:gap-2 font-mono text-text-muted uppercase tracking-wider",
            isSmallScreen ? "text-xs" : "text-sm"
          )}>
            <Brain className={cn("text-purple-400", isSmallScreen ? "w-3.5 h-3.5" : "w-4 h-4")} />
            {/* Show actual blueprint name based on mode */}
            {(() => {
              const mode = session.effectiveMode || session.requestedMode || 'auto';
              const modeConfig = RESEARCH_MODE_CONFIGS[mode as ResearchMode];
              const blueprintName = modeConfig?.name || (mode === 'auto' ? 'General Research' : mode);
              return (
                <span className="flex items-center gap-1.5">
                  {blueprintName}
                </span>
              );
            })()}
            {phase !== 'idle' && phase !== 'concluded' && (
              <span className={cn(
                "flex items-center gap-1 sm:gap-1.5 text-verdict-primary",
                isSmallScreen ? "ml-1" : "ml-2"
              )}>
                <Loader2 className="w-3 h-3 animate-spin" />
                <span className="hidden sm:inline">Processing...</span>
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* Smart Routing Intensity Indicator */}
            {session.effectiveIntensity && (
              <Tooltip content="Research depth and number of AI agents used" position="bottom">
                <div className={cn(
                  "flex items-center gap-1.5 px-2 py-1 rounded-lg bg-surface-light/60 border border-border/50 cursor-help",
                  isSmallScreen ? "text-[10px]" : "text-xs"
                )}>
                <Microscope className={cn(
                  "text-verdict-primary",
                  isSmallScreen ? "w-3 h-3" : "w-3.5 h-3.5"
                )} />
                <span className="text-text-secondary">
                  {INTENSITY_CONFIGS[session.effectiveIntensity]?.label || session.effectiveIntensity}
                </span>
                <span className="text-text-muted">|</span>
                <span className="text-text-muted">
                  {INTENSITY_CONFIGS[session.effectiveIntensity]?.agentCount || '?'} agents
                </span>
                {session.creditMultiplier && (
                  <>
                    <span className="text-text-muted">|</span>
                    <span className="text-verdict-primary font-medium">
                      {session.creditMultiplier}
                    </span>
                  </>
                )}
              </div>
              </Tooltip>
            )}
            {/* Collapse button - only show when research is complete */}
            {showAggregator && onToggleCollapse && (
              <button
                onClick={onToggleCollapse}
                className={cn(
                  "flex items-center gap-1 text-text-secondary hover:text-text-primary hover:bg-surface-light rounded-lg transition-colors",
                  isSmallScreen ? "px-2 py-1 text-xs" : "px-3 py-1.5 text-sm"
                )}
              >
                <ChevronUp className={cn(isSmallScreen ? "w-3.5 h-3.5" : "w-4 h-4")} />
                <span className="hidden sm:inline">Collapse</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Error Banner - Show when research encounters an error */}
      {phase === 'error' && error && (
        <div className={cn(
          "flex items-center gap-3 border-b border-red-500/20 bg-red-500/10",
          isSmallScreen ? "px-3 py-2" : "px-6 py-3"
        )}>
          <AlertTriangle className={cn("text-red-400 flex-shrink-0", isSmallScreen ? "w-4 h-4" : "w-5 h-5")} />
          <div className="flex-1 min-w-0">
            <p className={cn("text-red-300 font-medium", isSmallScreen ? "text-xs" : "text-sm")}>
              Research Interrupted
            </p>
            <p className={cn("text-red-400/80 truncate", isSmallScreen ? "text-[11px]" : "text-xs")}>
              {error}
            </p>
          </div>
          {onNewResearch && (
            <button
              onClick={onNewResearch}
              className={cn(
                "flex-shrink-0 px-3 py-1.5 rounded-lg bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 text-red-300 font-medium transition-colors",
                isSmallScreen ? "text-xs" : "text-sm"
              )}
            >
              Try Again
            </button>
          )}
        </div>
      )}

      {/* Tree Content - Scrollable area that fills remaining space */}
      <div ref={scrollContentRef} className={cn(
        "space-y-1 flex-1 min-h-0 overflow-y-auto",
        isSmallScreen ? "p-3" : "p-6"
      )}>
        {/* User Query Node */}
        <UserNode query={query} isActive={phase !== 'idle'} />

        {/* Planner Node */}
        {showPlanner && (
          <>
            <ConnectorLine />
            <PlannerNode
              isActive={phase === 'planning' || phase === 'submitting'}
              isComplete={tasks.length > 0}
              taskCount={tasks.length}
              tasks={tasks}
              revealedTasks={revealedTasks}
            />
          </>
        )}

        {/* Task Branches - Only show revealed tasks with animation */}
        {showTasks && revealedTasks > 0 && (
          <>
            <ConnectorLine isBranching taskCount={revealedTasks} isAnimating={revealedTasks < tasks.length} />
            {/* Group tasks by execution mode for visual clarity */}
            {(() => {
              const revealedTasksArray = tasks.slice(0, revealedTasks);
              // Group consecutive parallel tasks together
              const taskGroups: { mode: TaskExecutionMode; tasks: { task: TaskBreakdown; originalIndex: number }[] }[] = [];
              let currentGroup: { mode: TaskExecutionMode; tasks: { task: TaskBreakdown; originalIndex: number }[] } | null = null;

              revealedTasksArray.forEach((task, idx) => {
                const mode = task.execution_mode || 'parallel';
                if (!currentGroup || currentGroup.mode !== mode) {
                  currentGroup = { mode, tasks: [] };
                  taskGroups.push(currentGroup);
                }
                currentGroup.tasks.push({ task, originalIndex: idx });
              });

              // Build a map of task index -> parallel group indices for quick lookup
              const parallelGroupMap = new Map<number, number[]>();
              taskGroups.forEach(group => {
                if (group.mode === 'parallel' && group.tasks.length > 1) {
                  const indices = group.tasks.map(t => t.originalIndex);
                  indices.forEach(idx => parallelGroupMap.set(idx, indices));
                }
              });

              // Helper to check if a task is in the same parallel group as the current task
              const isTaskInCurrentParallelGroup = (taskIdx: number): boolean => {
                const currentParallelGroup = parallelGroupMap.get(currentTaskIndex);
                if (!currentParallelGroup) return taskIdx === currentTaskIndex;
                return currentParallelGroup.includes(taskIdx);
              };

              // Helper to check if all tasks in a parallel group are complete
              const isParallelGroupComplete = (taskIdx: number): boolean => {
                const group = parallelGroupMap.get(taskIdx);
                if (!group) {
                  // Not in a parallel group - use simple comparison
                  return taskIdx < currentTaskIndex || phase === 'concluded';
                }
                // For parallel groups, check if current task is past ALL tasks in the group
                const maxGroupIndex = Math.max(...group);
                return currentTaskIndex > maxGroupIndex || phase === 'concluded';
              };

              // Flatten all tasks into a single array with parallel group info
              const flatTasks = revealedTasksArray.map((task, idx) => ({
                task,
                originalIndex: idx,
                isInParallelGroup: parallelGroupMap.has(idx),
                parallelGroupIndices: parallelGroupMap.get(idx) || [],
              }));

              // Group tasks by their parallel group for rendering
              const renderGroups: { isParallel: boolean; tasks: typeof flatTasks }[] = [];
              let currentRenderGroup: { isParallel: boolean; tasks: typeof flatTasks } | null = null;

              flatTasks.forEach((taskData) => {
                const isParallel = taskData.isInParallelGroup;
                if (!currentRenderGroup || currentRenderGroup.isParallel !== isParallel) {
                  currentRenderGroup = { isParallel, tasks: [] };
                  renderGroups.push(currentRenderGroup);
                }
                currentRenderGroup.tasks.push(taskData);
              });

              return (
                <div className={cn("space-y-4", isSmallScreen ? "space-y-3" : "space-y-6")}>
                  {renderGroups.map((group, groupIdx) => {
                    if (group.isParallel) {
                      // Parallel tasks - wrap in a single dashed yellow container
                      // Dynamic grid columns based on task count - single column on mobile
                      const taskCount = group.tasks.length;
                      const gridColsClass = isSmallScreen
                        ? 'grid-cols-1'
                        : taskCount === 1
                          ? 'grid-cols-1'
                          : taskCount === 2
                            ? 'grid-cols-1 sm:grid-cols-2'
                            : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3';
                      return (
                        <div key={`parallel-group-${groupIdx}`} className={cn(
                          "relative inline-block w-auto",
                          isSmallScreen && "w-full"
                        )}>
                          {/* Single dashed yellow box around all parallel tasks */}
                          <div className={cn(
                            "absolute border-2 border-dashed border-amber-500/30 pointer-events-none",
                            isSmallScreen ? "-inset-1.5 rounded-xl" : "-inset-2 rounded-2xl"
                          )} />
                          {/* Parallel indicator badge */}
                          <div className={cn(
                            "absolute -top-3 left-1/2 -translate-x-1/2 z-10 bg-surface border border-amber-500/30 rounded font-mono text-amber-500/70 flex items-center gap-1 whitespace-nowrap",
                            isSmallScreen ? "px-1.5 py-0.5 text-[10px]" : "px-2 py-0.5 text-[11px]"
                          )}>
                            <Zap className={cn(isSmallScreen ? "w-2.5 h-2.5" : "w-3 h-3")} />
                            <span className="hidden xs:inline">Running in </span>parallel ({taskCount})
                          </div>
                          {/* Grid of parallel tasks inside the container - columns match task count */}
                          <div className={cn("grid pt-2", gridColsClass, isSmallScreen ? "gap-2" : "gap-4")}>
                            {group.tasks.map(({ task, originalIndex }) => (
                              <TaskBranch
                                key={originalIndex}
                                task={task}
                                taskIndex={originalIndex}
                                isExpanded={expandedTasks.has(originalIndex)}
                                isCurrent={isTaskInCurrentParallelGroup(originalIndex)}
                                isComplete={isParallelGroupComplete(originalIndex)}
                                onToggle={() => toggleTask(originalIndex)}
                                agents={agents}
                                debate={debatesPerTask?.get(originalIndex) || (debate?.taskIndex === originalIndex ? debate : null)}
                                streamingTexts={streamingTexts}
                                phase={phase}
                                reportData={reportData}
                                agentOutputs={agentOutputs}
                                taskAgentStatuses={taskAgentStatuses}
                                factCheckProgress={factCheckProgress}
                                onAgentClick={handleAgentClick}
                                isNewlyRevealed={originalIndex === revealedTasks - 1 && revealedTasks <= tasks.length}
                                isSmallScreen={isSmallScreen}
                                researchMode={researchMode}
                              />
                            ))}
                          </div>
                        </div>
                      );
                    } else {
                      // Sequential tasks - render in a grid without the yellow box
                      return (
                        <div key={`sequential-group-${groupIdx}`} className={cn(
                          "grid gap-4",
                          isSmallScreen ? "grid-cols-1 gap-2" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
                        )}>
                          {group.tasks.map(({ task, originalIndex }) => (
                            <TaskBranch
                              key={originalIndex}
                              task={task}
                              taskIndex={originalIndex}
                              isExpanded={expandedTasks.has(originalIndex)}
                              isCurrent={isTaskInCurrentParallelGroup(originalIndex)}
                              isComplete={isParallelGroupComplete(originalIndex)}
                              onToggle={() => toggleTask(originalIndex)}
                              agents={agents}
                              debate={debatesPerTask?.get(originalIndex) || (debate?.taskIndex === originalIndex ? debate : null)}
                              streamingTexts={streamingTexts}
                              phase={phase}
                              reportData={reportData}
                              agentOutputs={agentOutputs}
                              taskAgentStatuses={taskAgentStatuses}
                              factCheckProgress={factCheckProgress}
                              onAgentClick={handleAgentClick}
                              isNewlyRevealed={originalIndex === revealedTasks - 1 && revealedTasks <= tasks.length}
                              isSmallScreen={isSmallScreen}
                              researchMode={researchMode}
                            />
                          ))}
                        </div>
                      );
                    }
                  })}
                </div>
              );
            })()}
          </>
        )}

        {/* Final Aggregator Loading State */}
        {showAggregatorLoading && (
          <>
            <ConnectorLine isMerging taskCount={tasks.length} />
            <AggregatorLoadingNode />
          </>
        )}

        {/* Final Aggregator */}
        {showAggregator && (
          <>
            <ConnectorLine isMerging taskCount={tasks.length} />
            <AggregatorNode
              verdict={reportData?.verdict || ''}
              sources={reportData?.sources || []}
              duration={reportData?.durationSeconds}
              onNewResearch={onNewResearch}
              session={session}
              onFollowUp={showFollowUpButton ? handleFollowUp : undefined}
              hideActions={hideActions}
            />
          </>
        )}
      </div>

      {/* Agent Output Modal */}
      {showAgentModal && selectedAgent && (
        <AgentOutputModal
          agent={selectedAgent}
          onClose={() => {
            setShowAgentModal(false);
            setSelectedAgent(null);
          }}
        />
      )}

    </div>
  );
}


/** Agent Output Modal - Shows full agent response */
function AgentOutputModal({
  agent,
  onClose,
}: {
  agent: AgentOutputData;
  onClose: () => void;
}) {
  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Determine provider and color
  const providerKey = agent.provider.toLowerCase();
  const color = getProvColor(providerKey, 'hex');

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-surface border border-border rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col shadow-2xl animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border bg-surface/50">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: `${color}20`, color }}
            >
              {getProviderLogo(providerKey, 20)}
            </div>
            <div>
              <h2 className="text-lg font-semibold text-text-primary">{agent.agentName}</h2>
              <div className="flex items-center gap-3 text-xs text-text-muted">
                <span className="font-mono">{agent.provider}</span>
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {agent.latencySeconds.toFixed(1)}s
                </span>
                <span className="flex items-center gap-1">
                  <Globe className="w-3 h-3" />
                  {agent.webSourcesUsed} sources
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-surface-light rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-text-secondary" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="flex items-center gap-2 mb-3 text-xs text-text-muted">
            <FileText className="w-3 h-3" />
            <span>Task {agent.taskIndex + 1} • Agent {agent.agentIndex + 1}</span>
            {agent.success ? (
              <span className="flex items-center gap-1 text-emerald-500">
                <CheckCircle2 className="w-3 h-3" />
                Success
              </span>
            ) : (
              <span className="flex items-center gap-1 text-red-500">
                <AlertTriangle className="w-3 h-3" />
                Failed
              </span>
            )}
          </div>

          {/* Output content - render as markdown (full output) */}
          <div className="bg-surface/50 rounded-xl p-4 border border-border">
            {(agent.fullOutput || agent.outputPreview) ? (
              <MarkdownContent content={agent.fullOutput || agent.outputPreview} />
            ) : (
              <p className="text-text-muted italic">No output available</p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border bg-surface/30">
          <button
            onClick={onClose}
            className="w-full py-2 px-4 bg-surface-light hover:bg-surface-raised text-text-primary rounded-lg transition-colors text-sm font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}


/** SVG Connector Line between nodes */
function ConnectorLine({
  isBranching = false,
  isMerging = false,
  taskCount = 1,
  isAnimating = false,
}: {
  isBranching?: boolean;
  isMerging?: boolean;
  taskCount?: number;
  isAnimating?: boolean;
}) {
  if (isBranching || isMerging) {
    // Calculate width based on task count - wider spread for more tasks
    const svgWidth = Math.max(300, taskCount * 100);
    const startX = svgWidth / 2;
    // Spread endpoints evenly across the width
    const spread = Math.min(svgWidth - 100, (taskCount - 1) * 100);

    return (
      <div className="flex justify-center py-2">
        <svg width={svgWidth} height="40" className="text-text-muted">
          {Array.from({ length: taskCount }).map((_, i) => {
            // Calculate endpoint X position - evenly distributed
            const endX = taskCount === 1
              ? startX
              : startX - spread / 2 + (i * (spread / Math.max(taskCount - 1, 1)));
            const midY = 20;
            return (
              <path
                key={i}
                d={isBranching
                  ? `M ${startX} 0 Q ${startX} ${midY} ${endX} 40`
                  : `M ${endX} 0 Q ${endX} ${midY} ${startX} 40`
                }
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeDasharray="4 2"
                className={cn(
                  'transition-all duration-500',
                  isAnimating ? 'animate-pulse opacity-100' : 'opacity-70'
                )}
                style={{
                  animation: isAnimating ? `drawLine 0.5s ease-out ${i * 0.2}s forwards` : undefined,
                }}
              />
            );
          })}
        </svg>
      </div>
    );
  }

  // Simple vertical connector
  return (
    <div className="flex justify-center py-1">
      <div className="w-0.5 h-6 bg-gradient-to-b from-border-light to-border" />
    </div>
  );
}

/** User Query Node - Top of tree (Glass Dossier Style) */
function UserNode({ query, isActive }: { query: string; isActive: boolean }) {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const isSmallScreen = isMobile || isTablet;
  const [expanded, setExpanded] = useState(false);
  const truncated = query.length > 120;

  return (
    <div
      className={cn(
        'relative flex items-start border transition-all duration-500 overflow-hidden',
        'backdrop-blur-md bg-gradient-to-br rounded-xl',
        isSmallScreen ? 'gap-2 p-3' : 'gap-3 p-4',
        isActive
          ? 'from-blue-500/15 to-blue-600/5 border-blue-500/40 shadow-lg shadow-blue-500/10'
          : 'from-card-idle-from/60 to-card-idle-to/40 border-border/50'
      )}
      data-active={isActive}
    >
      {/* Subtle inner glow */}
      {isActive && (
        <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-blue-400/5 to-transparent pointer-events-none" />
      )}
      <div className={cn(
        'relative rounded-lg flex items-center justify-center transition-all duration-300 flex-shrink-0',
        isSmallScreen ? 'w-8 h-8' : 'w-10 h-10',
        isActive ? 'bg-blue-500/25 shadow-inner' : 'bg-surface-light/80'
      )}>
        <User className={cn(
          'transition-all duration-300',
          isSmallScreen ? 'w-4 h-4' : 'w-5 h-5',
          isActive ? 'text-blue-400 drop-shadow-[0_0_8px_rgba(59,130,246,0.5)]' : 'text-text-muted'
        )} />
      </div>
      <div className="relative flex-1 min-w-0">
        <div className={cn(
          "font-mono uppercase text-text-secondary tracking-wider",
          isSmallScreen ? "text-[11px]" : "text-xs"
        )}>User Query</div>
        <div className={cn(
          "text-text-primary font-medium break-words",
          isSmallScreen ? "text-sm" : "text-base",
          !expanded && truncated && "line-clamp-2"
        )}>
          {query || 'Awaiting query...'}
        </div>
        {truncated && (
          <button
            className="mt-1 text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1"
            onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
          >
            {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            {expanded ? 'Show less' : 'Show more'}
          </button>
        )}
      </div>
    </div>
  );
}

/** Planner Node - Breaks down query into tasks (Glass Dossier Style) */
function PlannerNode({
  isActive,
  isComplete,
  taskCount,
  tasks,
  revealedTasks,
}: {
  isActive: boolean;
  isComplete: boolean;
  taskCount: number;
  tasks: TaskBreakdown[];
  revealedTasks: number;
}) {
  const [showTasks, setShowTasks] = useState(true);
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const isSmallScreen = isMobile || isTablet;

  return (
    <div className="space-y-2">
      <div
        className={cn(
          'relative flex items-center border transition-all duration-500',
          'backdrop-blur-md bg-gradient-to-br rounded-xl',
          isSmallScreen ? 'gap-2 p-3' : 'gap-3 p-4',
          isActive
            ? 'from-purple-500/15 to-purple-600/5 border-purple-500/40 shadow-lg shadow-purple-500/10'
            : isComplete
            ? 'from-purple-500/10 to-purple-600/5 border-purple-500/30'
            : 'from-card-idle-from/60 to-card-idle-to/40 border-border/50'
        )}
        data-active={isActive}
      >
        {/* Subtle inner glow for active/complete */}
        {(isActive || isComplete) && (
          <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-purple-400/5 to-transparent pointer-events-none" />
        )}
        <div className={cn(
          'relative rounded-lg flex items-center justify-center transition-all duration-300',
          isSmallScreen ? 'w-8 h-8' : 'w-10 h-10',
          isActive ? 'bg-purple-500/25 shadow-inner' : isComplete ? 'bg-purple-500/20' : 'bg-surface-light/80'
        )}>
          {isActive ? (
            <Loader2 className={cn(
              "text-purple-400 animate-spin drop-shadow-[0_0_8px_rgba(168,85,247,0.5)]",
              isSmallScreen ? "w-4 h-4" : "w-5 h-5"
            )} />
          ) : isComplete ? (
            <>
              <Brain className={cn("text-purple-400", isSmallScreen ? "w-4 h-4" : "w-5 h-5")} />
              {/* Gold checkmark for completed */}
              <div className={cn(
                "absolute rounded-full bg-verdict-primary flex items-center justify-center shadow-lg shadow-verdict-primary/30",
                isSmallScreen ? "-top-0.5 -right-0.5 w-3.5 h-3.5" : "-top-1 -right-1 w-4 h-4"
              )}>
                <CheckCircle2 className={cn("text-black", isSmallScreen ? "w-2.5 h-2.5" : "w-3 h-3")} />
              </div>
            </>
          ) : (
            <Brain className={cn("text-text-muted", isSmallScreen ? "w-4 h-4" : "w-5 h-5")} />
          )}
        </div>
        <div className="relative flex-1 min-w-0">
          <div className={cn(
            "font-mono uppercase text-text-secondary tracking-wider",
            isSmallScreen ? "text-[11px]" : "text-xs"
          )}>Prompt Generator</div>
          <div className={cn(
            "text-text-primary font-medium",
            isSmallScreen ? "text-sm" : "text-base"
          )}>
            {isActive ? (
              <span className="truncate block">{isSmallScreen ? 'Breaking down...' : 'Breaking down query into research tasks...'}</span>
            ) : taskCount > 0 ? (
              `Created ${taskCount} ${isSmallScreen ? '' : 'research '}tasks`
            ) : (
              'Research planning'
            )}
          </div>
          {isActive && !isSmallScreen && (
            <div className="mt-1 text-sm text-purple-400/70 font-mono flex items-center gap-1">
              <span className="animate-pulse">|</span>
              Analyzing query structure...
            </div>
          )}
          {/* Execution mode legend */}
          {isComplete && tasks.length > 0 && (
            <div className={cn(
              "mt-1 flex items-center text-text-muted",
              isSmallScreen ? "gap-2 text-[11px]" : "gap-3 text-xs"
            )}>
              {(() => {
                const parallelCount = tasks.filter(t => (t.execution_mode || 'parallel') === 'parallel').length;
                const sequentialCount = tasks.length - parallelCount;
                return (
                  <>
                    {parallelCount > 0 && (
                      <Tooltip content="These tasks run at the same time to speed up research" position="top">
                        <span className="flex items-center gap-1 cursor-help">
                          <Zap className={cn("text-amber-500", isSmallScreen ? "w-2.5 h-2.5" : "w-3 h-3")} />
                          {parallelCount} {isSmallScreen ? 'par' : 'parallel'}
                        </span>
                      </Tooltip>
                    )}
                    {sequentialCount > 0 && (
                      <Tooltip content="These tasks run one after another, each building on previous results" position="top">
                        <span className="flex items-center gap-1 cursor-help">
                          <ArrowRight className={cn("text-blue-500", isSmallScreen ? "w-2.5 h-2.5" : "w-3 h-3")} />
                          {sequentialCount} {isSmallScreen ? 'seq' : 'sequential'}
                        </span>
                      </Tooltip>
                    )}
                  </>
                );
              })()}
            </div>
          )}
        </div>
        {/* Toggle to show/hide task prompts */}
        {isComplete && tasks.length > 0 && (
          <button
            onClick={() => setShowTasks(!showTasks)}
            className={cn(
              "hover:bg-purple-500/10 rounded-lg transition-colors flex-shrink-0",
              isSmallScreen ? "p-1.5" : "p-2"
            )}
            title={showTasks ? 'Hide task details' : 'Show task details'}
          >
            {showTasks ? (
              <ChevronDown className={cn("text-purple-400", isSmallScreen ? "w-3.5 h-3.5" : "w-4 h-4")} />
            ) : (
              <ChevronRight className={cn("text-purple-400", isSmallScreen ? "w-3.5 h-3.5" : "w-4 h-4")} />
            )}
          </button>
        )}
      </div>

      {/* Animated Task Prompts List */}
      {showTasks && tasks.length > 0 && (
        <div className={cn(
          "space-y-2 border-l-2 border-purple-500/20",
          isSmallScreen ? "ml-3 pl-2" : "ml-6 pl-4"
        )}>
          {tasks.map((task, idx) => (
            <div
              key={idx}
              className={cn(
                'rounded-lg border transition-all duration-500',
                isSmallScreen ? 'p-2' : 'p-3',
                idx < revealedTasks
                  ? 'bg-purple-500/5 border-purple-500/20 opacity-100 translate-x-0'
                  : 'bg-surface/30 border-border/50 opacity-50 -translate-x-2'
              )}
              style={{
                transitionDelay: `${idx * 100}ms`,
              }}
            >
              <div className={cn("flex items-start", isSmallScreen ? "gap-1.5" : "gap-2")}>
                <div className={cn(
                  'rounded flex items-center justify-center font-bold flex-shrink-0 mt-0.5',
                  isSmallScreen ? 'w-5 h-5 text-[11px]' : 'w-6 h-6 text-xs',
                  idx < revealedTasks ? 'bg-purple-500/20 text-purple-400' : 'bg-surface-light text-text-muted'
                )}>
                  {idx + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className={cn(
                    "font-medium text-text-primary truncate flex items-center gap-1",
                    isSmallScreen ? "text-xs" : "text-sm gap-1.5"
                  )}>
                    <ExecutionModeIcon mode={task.execution_mode || 'parallel'} />
                    {task.name}
                  </div>
                  <div className={cn(
                    "text-text-muted mt-0.5 line-clamp-2",
                    isSmallScreen ? "text-[11px]" : "text-xs"
                  )}>{task.description}</div>
                </div>
                {idx < revealedTasks && (
                  <CheckCircle2 className={cn(
                    "text-purple-400 flex-shrink-0",
                    isSmallScreen ? "w-3.5 h-3.5" : "w-4 h-4"
                  )} />
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}



/** Task Branch - Expandable section for each research task (Glass Dossier Style) */
function TaskBranch({
  task,
  taskIndex,
  isExpanded,
  isCurrent,
  isComplete,
  onToggle,
  agents,
  debate,
  streamingTexts,
  phase,
  reportData,
  agentOutputs,
  taskAgentStatuses,
  factCheckProgress,
  onAgentClick,
  isNewlyRevealed = false,
  isSmallScreen = false,
  researchMode = 'auto',
}: {
  task: TaskBreakdown;
  taskIndex: number;
  isExpanded: boolean;
  isCurrent: boolean;
  isComplete: boolean;
  onToggle: () => void;
  agents: ActiveAgent[];
  debate: DebateState | null;
  streamingTexts: Map<string, StreamingText>;
  phase: string;
  reportData: ResearchSession['reportData'];
  agentOutputs?: Map<string, AgentOutputData>;
  taskAgentStatuses?: Map<string, TaskAgentStatus>;
  factCheckProgress?: Map<number, FactCheckProgress>;
  onAgentClick?: (output: AgentOutputData) => void;
  isNewlyRevealed?: boolean;
  isSmallScreen?: boolean;
  researchMode?: ResearchMode;
}) {
  const hasDebate = debate?.isActive || (debate && !debate.isActive && debate.finalConsensus);
  const isDebating = debate?.isActive ?? false;
  const taskResult = reportData?.taskResults?.[taskIndex];
  const confidence = taskResult?.confidenceScore;

  // State for showing the full debate detail modal
  const [showDebateDetailModal, setShowDebateDetailModal] = useState(false);

  return (
    <div className={cn(
      // Grid item - takes full width of its grid cell
      'relative w-full rounded-xl border transition-all duration-500',
      'backdrop-blur-md bg-gradient-to-br overflow-hidden',
      isCurrent && !isComplete
        ? isDebating
          // Softer amber tones for debating state - less alarming
          ? 'from-amber-500/10 to-amber-600/5 border-amber-500/40 shadow-lg shadow-amber-500/10'
          // Muted teal/emerald instead of bright green
          : 'from-emerald-600/10 to-emerald-700/5 border-emerald-600/40 shadow-lg shadow-emerald-600/10'
        : isComplete
        ? 'from-card-idle-from/50 to-card-idle-to/30 border-verdict-primary/30'
        : 'from-card-idle-from/60 to-card-idle-to/40 border-border/50',
      // Animation for newly revealed tasks
      isNewlyRevealed && 'animate-in fade-in slide-in-from-bottom-4 duration-500'
    )}>
      {/* Subtle inner glow */}
      {(isCurrent || isComplete) && (
        <div className={cn(
          'absolute inset-0 rounded-xl bg-gradient-to-br pointer-events-none',
          isDebating ? 'from-amber-400/5 to-transparent' : isComplete ? 'from-verdict-primary/5 to-transparent' : 'from-emerald-600/5 to-transparent'
        )} />
      )}
      {/* Task Header */}
      <button
        onClick={onToggle}
        className={cn(
          "relative w-full flex items-center text-left hover:bg-surface-light transition-colors rounded-t-xl",
          isSmallScreen ? "gap-2 p-2" : "gap-3 p-3"
        )}
      >
        <div className={cn(
          'relative rounded-lg flex items-center justify-center font-bold transition-all duration-300',
          isSmallScreen ? 'w-7 h-7 text-xs' : 'w-8 h-8 text-sm',
          isCurrent && !isComplete
            // Softer amber for debating, muted emerald for in-progress
            ? isDebating ? 'bg-amber-500/25 text-amber-400 shadow-inner' : 'bg-emerald-600/25 text-emerald-500 shadow-inner'
            : isComplete
            ? 'bg-verdict-primary/25 text-verdict-primary'
            : 'bg-surface-light/80 text-text-muted'
        )}>
          {taskIndex + 1}
        </div>
        <div className="flex-1 min-w-0">
          <div className={cn(
            "font-medium text-text-primary truncate flex items-center gap-1",
            isSmallScreen ? "text-xs" : "text-sm gap-1.5"
          )}>
            <ExecutionModeIcon mode={task.execution_mode || 'parallel'} />
            {task.name}
            {task.required_capability && (
              <CapabilityBadge
                capability={task.required_capability}
                size="sm"
                iconOnly={isSmallScreen}
              />
            )}
          </div>
          <div className={cn(
            "text-text-muted truncate",
            isSmallScreen ? "text-[11px]" : "text-xs"
          )}>{task.description}</div>
        </div>
        <div className={cn("flex items-center", isSmallScreen ? "gap-1" : "gap-2")}>
          {confidence !== undefined && (
            <Tooltip
              content={confidence >= 0.8
                ? `Source agreement: ${(confidence * 100).toFixed(0)}%. Strong consensus across sources.`
                : `Source agreement: ${(confidence * 100).toFixed(0)}%. Low (<80%) triggers a debate.`
              }
              position="top"
            >
              <span
                className={cn(
                  'font-mono px-1.5 py-0.5 rounded backdrop-blur-sm cursor-help',
                  isSmallScreen ? 'text-[11px]' : 'text-xs',
                  // Muted emerald for high confidence
                  confidence >= 0.7 ? 'bg-emerald-600/20 text-emerald-500' : 'bg-red-500/20 text-red-400'
                )}
              >
                {(confidence * 100).toFixed(0)}%
              </span>
            </Tooltip>
          )}
          {/* Gold checkmark for completed tasks */}
          {isComplete && (
            <div className={cn(
              "rounded-full bg-verdict-primary flex items-center justify-center shadow-lg shadow-verdict-primary/30",
              isSmallScreen ? "w-4 h-4" : "w-5 h-5"
            )}>
              <CheckCircle2 className={cn(isSmallScreen ? "w-2.5 h-2.5" : "w-3.5 h-3.5", "text-black")} />
            </div>
          )}
          {isExpanded ? (
            <ChevronDown className={cn(isSmallScreen ? "w-3.5 h-3.5" : "w-4 h-4", "text-text-muted")} />
          ) : (
            <ChevronRight className={cn(isSmallScreen ? "w-3.5 h-3.5" : "w-4 h-4", "text-text-muted")} />
          )}
        </div>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className={cn(
          "space-y-2 border-t border-border-subtle",
          isSmallScreen ? "px-2 pb-2" : "px-3 pb-3"
        )}>
          {/* Agent Nodes - Only show agents that were actually used for this task */}
          <div className="pt-2 space-y-1.5">
            {agents.map((agent, idx) => {
              const outputKey = `${taskIndex}-${idx}`;
              const agentOutput = agentOutputs?.get(outputKey);
              // Get per-task agent status from tracking map
              const taskStatus = taskAgentStatuses?.get(outputKey);

              // Only render agents that have been used for this task
              // An agent is considered "used" if it has a status entry or output for this task
              const hasBeenUsed = taskStatus !== undefined || agentOutput !== undefined;

              // If no agents have status yet (early in research), show all agents
              // Otherwise, only show agents that were actually used
              const anyAgentHasStatus = agents.some((_, i) =>
                taskAgentStatuses?.has(`${taskIndex}-${i}`) || agentOutputs?.has(`${taskIndex}-${i}`)
              );

              // Skip rendering if this agent wasn't used (and we have evidence other agents were used)
              if (anyAgentHasStatus && !hasBeenUsed) {
                return null;
              }

              // Determine if this specific agent is active for this task
              const isAgentActive = taskStatus?.status === 'researching' || taskStatus?.status === 'thinking';
              const isAgentComplete = taskStatus?.status === 'complete';
              // Use a key that changes when status changes to force re-render
              const agentKey = `${agent.id}-${taskIndex}-${taskStatus?.status || 'idle'}`;
              return (
                <AgentNodeCompact
                  key={agentKey}
                  agent={agent}
                  agentIndex={idx}
                  taskIndex={taskIndex}
                  isActive={isCurrent && !isComplete && isAgentActive}
                  isAgentComplete={isAgentComplete}
                  streamingText={streamingTexts.get(`agent-${taskIndex}-${agent.id}`)}
                  agentOutput={agentOutput}
                  taskStatus={taskStatus}
                  onClick={agentOutput ? () => onAgentClick?.(agentOutput) : undefined}
                  researchMode={researchMode}
                />
              );
            })}
          </div>

          {/* Fact Checker */}
          {(isCurrent || isComplete) && (
            <FactCheckerNode
              taskIndex={taskIndex}
              confidence={confidence}
              isActive={isCurrent && !isComplete && phase === 'deliberating' && !debate}
              isComplete={confidence !== undefined}
              factCheckProgress={factCheckProgress?.get(taskIndex)}
              researchMode={researchMode}
            />
          )}

          {/* Debate Branch */}
          {hasDebate && (
            <DebateBranch
              debate={debate!}
              agents={agents}
              onShowFullDebate={() => setShowDebateDetailModal(true)}
            />
          )}

          {/* Source-backed Output */}
          {isComplete && taskResult?.aggregatedOutput && (
            <ValidatedOutputNode output={taskResult.aggregatedOutput} />
          )}
        </div>
      )}

      {/* Debate Detail Modal - shows full debate content when clicking completed debate card */}
      {showDebateDetailModal && debate && !debate.isActive && (
        <DebateDetailModal
          debate={debate}
          taskName={task.name}
          onClose={() => setShowDebateDetailModal(false)}
        />
      )}
    </div>
  );
}


/** Compact Agent Node for task branches */
function AgentNodeCompact({
  agent,
  agentIndex,
  taskIndex: _taskIndex,
  isActive,
  isAgentComplete,
  streamingText,
  agentOutput,
  taskStatus,
  onClick,
  researchMode = 'auto',
}: {
  agent: ActiveAgent;
  agentIndex: number;
  taskIndex: number;
  isActive: boolean;
  isAgentComplete?: boolean;
  streamingText?: StreamingText;
  agentOutput?: AgentOutputData;
  taskStatus?: TaskAgentStatus;
  onClick?: () => void;
  researchMode?: ResearchMode;
}) {
  // Use task-specific provider from agentOutput when available
  const provider = agentOutput?.provider?.toLowerCase() || agent.id;
  const detectedProvider = detectProvider(agentOutput?.model || agent.model || provider);

  // Get display name based on research mode
  // Financial mode: Show role names like "Risk Analyst", "Value Investor"
  // Other modes (auto/general, academic, strategic): Show model names like "GPT-4o Mini", "Claude 3.5 Haiku"
  const displayName = useMemo(() => {
    if (researchMode === 'financial') {
      // Financial blueprint uses role-based naming
      const agentRole = getAgentRoleForModel(researchMode, agentOutput?.model || agent.model, agentIndex);
      return agentRole.name;
    }
    // All other blueprints show model names directly
    return agentOutput?.model || agent.model || agent.name;
  }, [researchMode, agentOutput?.model, agent.model, agent.name, agentIndex]);

  const hasOutput = !!agentOutput?.outputPreview;
  const isClickable = hasOutput && onClick;

  // Determine visual state based on per-task status
  // Agent is "done" if taskStatus says complete OR if we have output
  const isDone = isAgentComplete || taskStatus?.status === 'complete';
  // Agent is "working" if active and not yet done
  const isWorking = isActive && !isDone;

  const statusText = useMemo(() => {
    if (isWorking) {
      if (taskStatus?.status === 'researching') return 'Searching web sources...';
      // 'thinking' status indicates reasoning model is processing (high TTFT)
      if (taskStatus?.status === 'thinking') return 'Thinking...';
      return 'Generating response...';
    }
    return null;
  }, [isWorking, taskStatus]);

  // Get individual streaming text for this specific agent
  const displayText = useMemo(() => {
    if (isWorking && streamingText) {
      return streamingText.displayedText;
    }
    if (agentOutput?.outputPreview) {
      // Show first 80 chars of actual output
      const preview = agentOutput.outputPreview.slice(0, 80);
      return preview + (agentOutput.outputPreview.length > 80 ? '...' : '');
    }
    return null;
  }, [isWorking, streamingText, agentOutput]);

  // Use per-task latency if available
  const latency = taskStatus?.latencySeconds ?? agentOutput?.latencySeconds;

  return (
    <div
      onClick={isClickable ? onClick : undefined}
      className={cn(
        'relative flex items-center gap-2 p-2 rounded-lg border transition-all duration-300',
        'backdrop-blur-sm bg-gradient-to-r',
        // Muted emerald background when actively working (spinning)
        isWorking
          ? 'from-emerald-600/15 to-emerald-700/5 border-emerald-600/40 shadow-md shadow-emerald-600/10'
          // Muted emerald completed state when done (checkmark)
          : isDone
          ? 'from-emerald-600/10 to-emerald-700/5 border-emerald-600/30'
          // Default idle state
          : 'from-card-idle-from/50 to-card-idle-to/30 border-border/40',
        isClickable && 'cursor-pointer hover:from-card-idle-from/80 hover:to-card-idle-from/60 hover:border-border-light/50'
      )}
      title={isClickable ? 'Click to view full agent output' : undefined}
    >
      {/* Subtle inner glow for active state */}
      {isWorking && (
        <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-emerald-600/5 to-transparent pointer-events-none" />
      )}
      <div
        className={cn(
          'relative w-6 h-6 rounded flex items-center justify-center flex-shrink-0 transition-all duration-300',
          isWorking ? 'bg-emerald-600/25 shadow-inner' : isDone ? `bg-surface-light/60` : 'bg-surface-light/80'
        )}
        style={{
          borderColor: isWorking ? agent.color : undefined,
          backgroundColor: isDone ? `${agent.color}20` : undefined
        }}
      >
        {isWorking ? (
          <Loader2
            className="w-3 h-3 animate-spin"
            style={{
              color: agent.color,
              filter: `drop-shadow(0 0 6px ${agent.color})`
            }}
          />
        ) : (
          // Keep model icon with distinct color for both idle and done states
          <span className="transition-all duration-300" style={{ color: agent.color }}>
            {getProviderLogo(detectedProvider, 12)}
          </span>
        )}
      </div>
      <div className="relative flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={cn(
            'text-xs font-mono truncate',
            // Muted emerald text for done state
            isDone ? 'text-emerald-500' : 'text-text-secondary'
          )}>{displayName}</span>
          {latency != null && latency > 0 && (
            <span className={cn(
              'text-[10px]',
              isDone ? 'text-emerald-600/60' : 'text-text-muted'
            )}>{latency.toFixed(1)}s</span>
          )}
          {isDone && !isWorking && (
            <span className="text-[10px] text-verdict-primary font-medium inline-flex items-center gap-0.5 leading-none">
              <span>Done</span>
            </span>
          )}
        </div>
        {/* Show streaming text or output preview */}
        {displayText && (
          <div className={cn(
            'text-[11px] font-mono truncate flex items-center gap-1',
            // Muted emerald for working state text
            isWorking ? 'text-emerald-500/70' : 'text-text-muted'
          )}>
            {displayText}
            {isWorking && !streamingText?.isComplete && <span className="animate-pulse">|</span>}
          </div>
        )}
        {isWorking && !displayText && statusText && (
          <div className="text-[11px] text-emerald-500/70 font-mono flex items-center gap-1">
            {statusText}
            <span className="animate-pulse">|</span>
          </div>
        )}
      </div>
      {/* Pulsing indicator for active state - muted emerald */}
      {isWorking && (
        <div className="relative w-2 h-2 flex-shrink-0">
          <div className="absolute inset-0 rounded-full bg-emerald-600 animate-ping opacity-75" />
          <div className="relative w-2 h-2 rounded-full bg-emerald-500" />
        </div>
      )}
      {isClickable && !isWorking && (
        <ChevronRight className="w-3 h-3 text-text-muted flex-shrink-0" />
      )}
    </div>
  );
}

/** Fact Checker Node - Distinct "Verdict Forming" Visual Treatment */
function FactCheckerNode({
  taskIndex: _taskIndex,
  confidence,
  isActive,
  isComplete,
  factCheckProgress,
  researchMode = 'auto',
}: {
  taskIndex: number;
  confidence?: number;
  isActive: boolean;
  isComplete: boolean;
  factCheckProgress?: FactCheckProgress;
  researchMode?: ResearchMode;
}) {
  // Use fact-check progress data if available, otherwise fall back to props
  const isFactCheckActive = factCheckProgress?.isActive || isActive;
  const isFactCheckComplete = factCheckProgress?.isComplete || isComplete;
  const actualConfidence = factCheckProgress?.confidenceScore ?? confidence;
  const discrepancies = factCheckProgress?.discrepancies || [];
  const keyFindings = factCheckProgress?.keyFindings || [];
  const needsDebate = factCheckProgress?.needsDebate || false;
  const model = factCheckProgress?.model || 'Preparing...';
  const provider = factCheckProgress?.provider || '';

  // Show "waiting" state if we haven't started and aren't complete
  // This happens briefly between aggregation and fact-check start
  const isWaiting = !isFactCheckActive && !isFactCheckComplete && isActive;

  // Low confidence is below 80% - this triggers red styling and debate
  const isLowConfidence = actualConfidence !== undefined && actualConfidence < 0.8;
  // High confidence is 80% or above - this shows green styling with consensus
  const isHighConfidence = actualConfidence !== undefined && actualConfidence >= 0.8;

  // Model icon based on provider
  const providerLogoKey = detectProvider(provider);

  // Parse discrepancies into point/counterpoint format for FINANCIAL mode only
  // Other modes (auto/general, academic, strategic) show simple discrepancy list
  const conflictingViewpoints = useMemo(() => {
    if (!discrepancies.length) return null;

    // Only parse into point/counterpoint for financial mode
    if (researchMode === 'financial') {
      // Try to extract two opposing viewpoints from discrepancies
      // Format: "Agent X says A, but Agent Y says B" or similar patterns
      const viewpoints: { pointA: string; pointB: string }[] = [];
      discrepancies.forEach(d => {
        // Try to split on common conflict patterns
        const patterns = [
          /(.+?)\s*(?:but|however|while|whereas)\s*(.+)/i,
          /(.+?)\s*(?:vs\.?|versus)\s*(.+)/i,
          /Agent \d+ (?:says|reports|claims|argues)\s*(.+?),?\s*(?:but|while|whereas)\s*Agent \d+ (?:says|reports|claims|argues|found)\s*(.+)/i,
        ];
        for (const pattern of patterns) {
          const match = d.match(pattern);
          if (match) {
            viewpoints.push({ pointA: match[1].trim(), pointB: match[2].trim() });
            return;
          }
        }
        // If no pattern matches, just use the whole discrepancy
        viewpoints.push({ pointA: d, pointB: '' });
      });
      return viewpoints.length > 0 ? viewpoints : null;
    }

    // For non-financial modes, return null to trigger simple list display
    return null;
  }, [discrepancies, researchMode]);

  return (
    <div className={cn(
      'relative p-3 rounded-xl border-2 transition-all duration-500 overflow-hidden',
      'backdrop-blur-md bg-gradient-to-br',
      // Distinct visual treatment - larger, more prominent
      // Using original muted yellow for active, original green for success
      isFactCheckActive
        ? 'from-yellow-500/10 to-amber-600/5 border-yellow-500/30 shadow-lg shadow-yellow-500/10'
        : isLowConfidence || needsDebate
        ? 'from-red-500/10 to-orange-600/5 border-red-500/30 shadow-lg shadow-red-500/10'
        : isHighConfidence
        ? 'from-yellow-500/5 to-amber-600/5 border-yellow-500/20'
        : 'from-card-idle-from/60 to-card-idle-to/40 border-border/50'
    )}>
      {/* Inner glow effect for "verdict forming" feel */}
      <div className={cn(
        'absolute inset-0 rounded-xl pointer-events-none',
        isFactCheckActive && 'bg-gradient-to-br from-yellow-400/5 via-transparent to-amber-400/5 animate-pulse',
        (isLowConfidence || needsDebate) && 'bg-gradient-to-br from-red-400/5 via-transparent to-orange-400/5'
      )} />

      <div className="relative flex items-center gap-3">
        {/* Model icon - shows provider icon with gold checkmark when complete */}
        <div className={cn(
          'relative w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-300',
          isFactCheckActive ? 'bg-yellow-500/20'
            : isLowConfidence || needsDebate ? 'bg-red-500/20'
            : isHighConfidence ? 'bg-yellow-500/10'
            : 'bg-surface-light/80'
        )}>
          {isFactCheckActive ? (
            <Loader2 className="w-5 h-5 text-yellow-400 animate-spin" />
          ) : isLowConfidence || needsDebate ? (
            <AlertTriangle className="w-5 h-5 text-red-400" />
          ) : (
            <span className={cn(isHighConfidence ? 'text-yellow-400' : 'text-text-muted')}>{getProviderLogo(providerLogoKey, 20)}</span>
          )}
          {/* Gold checkmark overlay for completed state */}
          {isFactCheckComplete && isHighConfidence && !needsDebate && (
            <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-verdict-primary flex items-center justify-center shadow-lg shadow-verdict-primary/30">
              <CheckCircle2 className="w-3 h-3 text-black" />
            </div>
          )}
        </div>
        <div className="relative flex-1 min-w-0">
          <div className={cn(
            'text-sm font-mono font-medium',
            isFactCheckActive ? 'text-text-primary' : isHighConfidence ? 'text-text-primary' : isLowConfidence ? 'text-red-400' : 'text-text-secondary'
          )}>Fact-Checker</div>
          <div className="text-xs text-text-muted">{model}</div>
          {isWaiting && (
            <div className="text-xs text-text-secondary/80 font-mono flex items-center gap-1 mt-1">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-text-muted animate-pulse" />
              Preparing verification...
            </div>
          )}
          {isFactCheckActive && !isWaiting && (
            <div className="text-xs text-yellow-400/80 font-mono flex items-center gap-1 mt-1">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-yellow-400 animate-pulse" />
              Validating claims against sources...
            </div>
          )}
          {isFactCheckComplete && actualConfidence !== undefined && (
            <div className={cn(
              'text-xs font-mono font-medium mt-1',
              isLowConfidence ? 'text-red-400' : 'text-verdict-primary'
            )}>
              {isLowConfidence || needsDebate
                ? `Source agreement: ${(actualConfidence * 100).toFixed(0)}% - Debate required`
                : `Source agreement: ${(actualConfidence * 100).toFixed(0)}%`
              }
            </div>
          )}
        </div>
      </div>

      {/* DISCREPANCIES - Always show when they exist, regardless of confidence */}
      {/* Financial mode: Point/Counterpoint format */}
      {/* Other modes: Simple discrepancy list */}
      {isFactCheckComplete && discrepancies.length > 0 && (
        <div className="relative mt-3 pt-3 border-t border-red-400/30">
          {/* Discrepancy Header - Show prominently regardless of confidence */}
          <div className="flex items-center gap-2 mb-2">
            <div className={cn(
              'flex items-center gap-1.5 px-2 py-1 rounded-md border',
              isLowConfidence || needsDebate
                ? 'bg-red-400/15 border-red-400/25 animate-debate-pulse'
                : 'bg-amber-500/15 border-amber-500/25'
            )}>
              <AlertTriangle className={cn(
                'w-3.5 h-3.5',
                isLowConfidence || needsDebate ? 'text-red-300' : 'text-amber-400'
              )} />
              <span className={cn(
                'text-xs font-mono font-bold uppercase tracking-wider',
                isLowConfidence || needsDebate ? 'text-red-300' : 'text-amber-400'
              )}>
                {isLowConfidence || needsDebate ? 'Conflict Detected' : 'Discrepancies Found'}
              </span>
            </div>
          </div>

          {/* Financial mode: Point/Counterpoint Grid */}
          {researchMode === 'financial' && conflictingViewpoints && conflictingViewpoints.length > 0 && (
            <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
              {conflictingViewpoints.map((vp, idx) => (
                <div key={idx} className="grid grid-cols-2 gap-2">
                  {/* Point A - Position */}
                  <div className="p-2 rounded-lg bg-blue-500/10 border border-blue-500/20">
                    <div className="flex items-center gap-1 mb-1">
                      <div className="w-5 h-5 rounded-full bg-blue-500/30 flex items-center justify-center">
                        <span className="text-[10px] font-bold text-blue-400">A</span>
                      </div>
                      <span className="text-[10px] font-mono text-blue-400 uppercase">Position</span>
                    </div>
                    <div className="text-[11px] text-text-primary whitespace-pre-wrap">{vp.pointA}</div>
                  </div>
                  {/* Point B - Counterpoint */}
                  {vp.pointB && (
                    <div className="p-2 rounded-lg bg-orange-500/10 border border-orange-500/20">
                      <div className="flex items-center gap-1 mb-1">
                        <div className="w-5 h-5 rounded-full bg-orange-500/30 flex items-center justify-center">
                          <span className="text-[10px] font-bold text-orange-400">B</span>
                        </div>
                        <span className="text-[10px] font-mono text-orange-400 uppercase">Counterpoint</span>
                      </div>
                      <div className="text-[11px] text-text-primary whitespace-pre-wrap">{vp.pointB}</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Non-financial modes: Simple discrepancy list */}
          {researchMode !== 'financial' && (
            <ul className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
              {discrepancies.map((d, idx) => (
                <li key={idx} className="flex items-start gap-2 text-[11px] text-text-primary">
                  <span className="text-amber-400 mt-0.5">-</span>
                  <span className="whitespace-pre-wrap">{d}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Show consensus points (key findings) when we have valid findings */}
      {/* Only show if NO discrepancies exist (discrepancies shown above are more important) */}
      {(() => {
        // Don't show consensus if still processing or if discrepancies exist
        if (!isFactCheckComplete || discrepancies.length > 0) {
          return null;
        }

        const validFindings = keyFindings.filter(f =>
          !f.toLowerCase().includes('encountered an error') &&
          !f.toLowerCase().includes('unable to parse') &&
          !f.toLowerCase().includes('response parsed from text')
        );
        const hasValidFindings = validFindings.length > 0;

        // Only show if we have actual consensus points to display
        if (!hasValidFindings && !isHighConfidence) {
          return null;
        }

        return (
          <div className="relative mt-3 pt-3 border-t border-emerald-600/30">
            <div className="flex items-center gap-1.5 mb-2">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
              <span className="text-xs font-mono text-emerald-500/80">
                {hasValidFindings ? 'Consensus points:' : 'Full Agreement'}
              </span>
            </div>
            {hasValidFindings ? (
              <ul className="space-y-1 max-h-32 overflow-y-auto pr-1">
                {validFindings.map((f, idx) => (
                  <li key={idx} className="text-[11px] text-text-primary pl-2 border-l-2 border-emerald-600/40 bg-emerald-600/5 rounded-r py-1 pr-2">
                    {f}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-[11px] text-text-secondary pl-2 border-l-2 border-emerald-600/40 bg-emerald-600/5 rounded-r py-1 pr-2">
                All agents reached agreement - no discrepancies found
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}


/** Debate Branch - Distinct "Verdict Forming" Visual Treatment */
function DebateBranch({
  debate,
  agents: _agents,
  onShowFullDebate,
}: {
  debate: DebateState;
  agents: ActiveAgent[];
  onShowFullDebate?: () => void;
}) {
  const isActive = debate.isActive;
  const consensusReached = !debate.isActive && debate.consensusProgress >= 0.7;
  const isClickable = !isActive && onShowFullDebate;

  // Group arguments by agent for side-by-side display
  const argumentsByAgent = useMemo(() => {
    const grouped = new Map<string, typeof debate.arguments>();
    debate.arguments.forEach(arg => {
      const existing = grouped.get(arg.agentName) || [];
      grouped.set(arg.agentName, [...existing, arg]);
    });
    return grouped;
  }, [debate.arguments]);

  const agentNames = Array.from(argumentsByAgent.keys());

  // Create a chronological list of all arguments for conversation view
  const chronologicalArguments = useMemo(() => {
    // Flatten and sort by round number, then by order of appearance
    const allArgs: Array<{ agentName: string; argument: string; roundNumber: number; agentIndex: number }> = [];
    agentNames.forEach((agentName, agentIndex) => {
      const args = argumentsByAgent.get(agentName) || [];
      args.forEach(arg => {
        allArgs.push({ ...arg, agentIndex });
      });
    });
    // Sort by round number first, then by agent index within each round
    return allArgs.sort((a, b) => {
      if (a.roundNumber !== b.roundNumber) return a.roundNumber - b.roundNumber;
      return a.agentIndex - b.agentIndex;
    });
  }, [argumentsByAgent, agentNames]);

  // For completed debates, show a condensed clickable card
  if (!isActive && debate.finalConsensus) {
    return (
      <div
        className={cn(
          'relative rounded-xl border-2 p-3 space-y-2 transition-all duration-300 overflow-hidden',
          'backdrop-blur-md bg-gradient-to-br cursor-pointer group',
          consensusReached
            ? 'from-verdict-primary/15 to-amber-600/10 border-verdict-primary/50 shadow-lg shadow-verdict-primary/20 hover:shadow-xl hover:shadow-verdict-primary/30'
            : 'from-amber-500/10 to-orange-600/5 border-amber-500/40 hover:border-amber-500/60',
          isClickable && 'hover:scale-[1.01]'
        )}
        onClick={onShowFullDebate}
        role={isClickable ? 'button' : undefined}
        tabIndex={isClickable ? 0 : undefined}
        onKeyDown={isClickable ? (e) => e.key === 'Enter' && onShowFullDebate?.() : undefined}
      >
        {/* Background glow */}
        {consensusReached && (
          <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-verdict-primary/10 via-transparent to-amber-400/5 pointer-events-none" />
        )}

        {/* Header with consensus badge */}
        <div className="relative flex items-center gap-3">
          <div className={cn(
            'w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-300',
            consensusReached ? 'bg-verdict-primary/25' : 'bg-amber-500/20'
          )}>
            {consensusReached ? (
              <Scale className="w-4 h-4 text-verdict-primary" />
            ) : (
              <MessageSquare className="w-4 h-4 text-amber-400" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className={cn(
              'text-xs font-mono font-bold uppercase tracking-wider',
              consensusReached ? 'text-verdict-primary' : 'text-amber-400'
            )}>
              {consensusReached ? 'Consensus Reached' : 'Discussion Concluded'}
            </div>
            <div className="text-[11px] text-text-muted font-mono">
              {debate.arguments.length} exchanges - {(debate.consensusProgress * 100).toFixed(0)}% agreement
            </div>
          </div>
          {/* Click hint */}
          <div className="flex items-center gap-1 text-[11px] text-text-muted group-hover:text-text-primary transition-colors">
            <span>View details</span>
            <ChevronRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
          </div>
        </div>

        {/* Discrepancies summary (collapsed) */}
        {debate.discrepancies && debate.discrepancies.length > 0 && (
          <div className="flex items-start gap-2 px-2 py-1.5 rounded-lg bg-surface-light/30">
            <AlertTriangle className="w-3 h-3 text-amber-400 mt-0.5 flex-shrink-0" />
            <div className="text-[11px] text-text-secondary line-clamp-1">
              {debate.discrepancies.length === 1
                ? debate.discrepancies[0]
                : `${debate.discrepancies.length} points discussed`
              }
            </div>
          </div>
        )}

        {/* Final consensus preview - truncated */}
        <div className="relative p-2 rounded-lg bg-gradient-to-r from-verdict-primary/10 to-amber-500/5 border border-verdict-primary/30">
          <div className="text-xs text-text-primary line-clamp-2 leading-relaxed">
            <MarkdownContent content={debate.finalConsensus || ''} className="text-xs" inline />
          </div>
        </div>
      </div>
    );
  }

  // For active debates, show the full content
  return (
    <div className={cn(
      'relative rounded-xl border-2 p-3 space-y-3 transition-all duration-500 overflow-hidden',
      'backdrop-blur-md bg-gradient-to-br',
      // Softer amber/orange tones instead of red - less alarming
      'from-amber-500/12 to-orange-500/8 border-amber-500/40 shadow-lg shadow-amber-500/10'
    )}>
      {/* Subtle inner glow for active debate - no animation */}
      <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-amber-400/5 via-transparent to-orange-400/5 pointer-events-none" />

      {/* Debate Header */}
      <div className="relative flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-amber-500/20 shadow-inner">
          <MessageSquare className="w-5 h-5 text-amber-400" />
        </div>
        <div className="flex-1">
          <div className="text-sm font-mono font-bold uppercase tracking-wider text-amber-400">
            Debate in Progress
          </div>
          <div className="text-xs text-amber-400/70 font-mono">
            {debate.speakingAgent === 'Mediator' && debate.arguments.length > 0
              ? 'Mediator forming consensus...'
              : `Round ${debate.currentRound} - Resolving discrepancies...`}
          </div>
        </div>
        {/* Progress ring */}
        <div className="w-8 h-8">
          <svg className="w-8 h-8 -rotate-90" viewBox="0 0 32 32">
            <circle cx="16" cy="16" r="12" fill="none" stroke="currentColor" strokeWidth="2" className="text-text-muted" />
            <circle
              cx="16" cy="16" r="12" fill="none" stroke="currentColor" strokeWidth="2"
              className="text-amber-400"
              strokeDasharray="75.4"
              strokeDashoffset={75.4 * (1 - debate.consensusProgress)}
              strokeLinecap="round"
              style={{ transition: 'stroke-dashoffset 0.5s ease-out' }}
            />
          </svg>
        </div>
      </div>

      {/* Mediator Opening */}
      {debate.mediatorOpening && (
        <div className="relative p-3 rounded-lg backdrop-blur-sm bg-gradient-to-r from-cyan-500/15 to-cyan-600/5 border border-cyan-500/30">
          <div className="flex items-center gap-2 text-xs text-cyan-400 font-mono mb-1.5">
            <Users className="w-4 h-4" />
            <span className="font-medium">Mediator</span>
          </div>
          <div className="text-sm text-text-primary leading-relaxed">
            <MarkdownContent content={debate.mediatorOpening} className="text-sm" />
          </div>
        </div>
      )}

      {/* Debate Arguments - Conversation Style */}
      {debate.arguments.length > 0 && (
        <div className="space-y-2">
          {chronologicalArguments.map((arg, idx) => {
            const isLeftAligned = arg.agentIndex % 2 === 0;
            const isCurrentSpeaker = debate.speakingAgent === arg.agentName;
            const colorScheme = isLeftAligned
              ? { bg: 'from-blue-500/15 to-blue-600/8', border: 'border-blue-500/30', text: 'text-blue-400', badge: 'bg-blue-500/30' }
              : { bg: 'from-orange-500/15 to-orange-600/8', border: 'border-orange-500/30', text: 'text-orange-400', badge: 'bg-orange-500/30' };

            return (
              <div
                key={idx}
                className={cn(
                  'flex',
                  isLeftAligned ? 'justify-start' : 'justify-end'
                )}
              >
                <div
                  className={cn(
                    'max-w-[85%] p-3 rounded-xl border transition-all duration-300',
                    'backdrop-blur-sm bg-gradient-to-br',
                    colorScheme.bg,
                    colorScheme.border,
                    isCurrentSpeaker && 'ring-2 ring-amber-400/40 shadow-lg',
                    isLeftAligned ? 'rounded-tl-sm' : 'rounded-tr-sm'
                  )}
                >
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <div className={cn(
                      'w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold',
                      colorScheme.badge,
                      colorScheme.text
                    )}>
                      {isLeftAligned ? 'A' : 'B'}
                    </div>
                    <span className={cn('text-xs font-mono font-medium', colorScheme.text)}>
                      {arg.agentName}
                    </span>
                    <span className="text-[11px] text-text-muted font-mono">R{arg.roundNumber}</span>
                    {isCurrentSpeaker && (
                      <span className="ml-auto w-1.5 h-1.5 rounded-full bg-amber-400" />
                    )}
                  </div>
                  <div className="text-xs text-text-primary leading-relaxed">
                    {arg.argument}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Debate Detail Modal - Full-screen view of completed debate content */
function DebateDetailModal({
  debate,
  taskName,
  onClose,
}: {
  debate: DebateState;
  taskName: string;
  onClose: () => void;
}) {
  const contentRef = useRef<HTMLDivElement>(null);
  const consensusReached = debate.consensusProgress >= 0.7;

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Group arguments by agent for display
  const argumentsByAgent = useMemo(() => {
    const grouped = new Map<string, typeof debate.arguments>();
    debate.arguments.forEach(arg => {
      const existing = grouped.get(arg.agentName) || [];
      grouped.set(arg.agentName, [...existing, arg]);
    });
    return grouped;
  }, [debate.arguments]);

  const agentNames = Array.from(argumentsByAgent.keys());

  // Chronological list of arguments
  const chronologicalArguments = useMemo(() => {
    const allArgs: Array<{ agentName: string; argument: string; roundNumber: number; agentIndex: number }> = [];
    agentNames.forEach((agentName, agentIndex) => {
      const args = argumentsByAgent.get(agentName) || [];
      args.forEach(arg => {
        allArgs.push({ ...arg, agentIndex });
      });
    });
    return allArgs.sort((a, b) => {
      if (a.roundNumber !== b.roundNumber) return a.roundNumber - b.roundNumber;
      return a.agentIndex - b.agentIndex;
    });
  }, [argumentsByAgent, agentNames]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div
        className={cn(
          'relative w-full max-w-3xl max-h-[85vh] rounded-2xl border-2 overflow-hidden',
          'backdrop-blur-md bg-gradient-to-br shadow-2xl',
          consensusReached
            ? 'from-verdict-primary/15 to-amber-600/10 border-verdict-primary/50 shadow-verdict-primary/30'
            : 'from-amber-500/10 to-orange-600/5 border-amber-500/40'
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between p-4 border-b border-border-subtle bg-bar-bg/50 backdrop-blur-sm">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className={cn(
              'w-10 h-10 rounded-lg flex items-center justify-center',
              consensusReached ? 'bg-verdict-primary/25' : 'bg-amber-500/20'
            )}>
              {consensusReached ? (
                <Scale className="w-5 h-5 text-verdict-primary" />
              ) : (
                <MessageSquare className="w-5 h-5 text-amber-400" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <h3 className={cn(
                'text-sm font-mono font-bold uppercase tracking-wider',
                consensusReached ? 'text-verdict-primary' : 'text-amber-400'
              )}>
                {consensusReached ? 'Consensus Reached' : 'Discussion Summary'}
              </h3>
              <p className="text-xs text-text-secondary truncate">{taskName}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className={cn(
              'px-2 py-1 rounded-full text-xs font-bold',
              consensusReached ? 'bg-verdict-primary/20 text-verdict-primary' : 'bg-amber-500/20 text-amber-400'
            )}>
              {(debate.consensusProgress * 100).toFixed(0)}% Agreement
            </span>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-surface-light transition-colors"
            >
              <X className="w-5 h-5 text-text-secondary" />
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div ref={contentRef} className="overflow-y-auto max-h-[calc(85vh-80px)] p-4 space-y-4">
          {/* Discrepancies that were discussed */}
          {debate.discrepancies && debate.discrepancies.length > 0 && (
            <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
              <div className="flex items-center gap-2 text-xs text-amber-400 font-mono mb-2">
                <AlertTriangle className="w-4 h-4" />
                <span className="font-medium">Points Discussed:</span>
              </div>
              <ul className="space-y-1">
                {debate.discrepancies.map((d, idx) => (
                  <li key={idx} className="text-xs text-text-primary pl-3 border-l-2 border-amber-500/40">
                    {d}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Mediator Opening */}
          {debate.mediatorOpening && (
            <div className="p-3 rounded-lg backdrop-blur-sm bg-gradient-to-r from-cyan-500/15 to-cyan-600/5 border border-cyan-500/30">
              <div className="flex items-center gap-2 text-xs text-cyan-400 font-mono mb-2">
                <Users className="w-4 h-4" />
                <span className="font-medium">Facilitator Opening</span>
              </div>
              <div className="text-sm text-text-primary leading-relaxed">
                <MarkdownContent content={debate.mediatorOpening} className="text-sm" />
              </div>
            </div>
          )}

          {/* Discussion Arguments */}
          {chronologicalArguments.length > 0 && (
            <div className="space-y-3">
              <div className="text-xs text-text-muted font-mono uppercase tracking-wider">
                Discussion ({chronologicalArguments.length} exchanges)
              </div>
              {chronologicalArguments.map((arg, idx) => {
                const isLeftAligned = arg.agentIndex % 2 === 0;
                const colorScheme = isLeftAligned
                  ? { bg: 'from-blue-500/15 to-blue-600/8', border: 'border-blue-500/30', text: 'text-blue-400', badge: 'bg-blue-500/30' }
                  : { bg: 'from-orange-500/15 to-orange-600/8', border: 'border-orange-500/30', text: 'text-orange-400', badge: 'bg-orange-500/30' };

                return (
                  <div
                    key={idx}
                    className={cn('flex', isLeftAligned ? 'justify-start' : 'justify-end')}
                  >
                    <div
                      className={cn(
                        'max-w-[85%] p-3 rounded-xl border',
                        'backdrop-blur-sm bg-gradient-to-br',
                        colorScheme.bg,
                        colorScheme.border,
                        isLeftAligned ? 'rounded-tl-sm' : 'rounded-tr-sm'
                      )}
                    >
                      <div className="flex items-center gap-1.5 mb-2">
                        <div className={cn(
                          'w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold',
                          colorScheme.badge,
                          colorScheme.text
                        )}>
                          {isLeftAligned ? 'A' : 'B'}
                        </div>
                        <span className={cn('text-xs font-mono font-medium', colorScheme.text)}>
                          {arg.agentName}
                        </span>
                        <span className="text-[11px] text-text-muted font-mono">Round {arg.roundNumber}</span>
                      </div>
                      <MarkdownContent content={arg.argument} className="text-sm text-text-primary leading-relaxed" inline />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Final Consensus */}
          {debate.finalConsensus && (
            <div className="relative p-4 rounded-lg backdrop-blur-sm bg-gradient-to-r from-verdict-primary/20 to-amber-500/10 border border-verdict-primary/40">
              <div className="absolute top-0 left-4 right-4 h-0.5 bg-gradient-to-r from-transparent via-verdict-primary to-transparent" />
              <div className="flex items-center gap-2 text-sm text-verdict-primary font-mono mb-3 pt-1">
                <Scale className="w-5 h-5" />
                <span className="font-bold uppercase tracking-wider">Final Consensus</span>
              </div>
              <MarkdownContent content={debate.finalConsensus || ''} className="text-sm text-text-primary leading-relaxed" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** Source-backed Output Node */
function ValidatedOutputNode({ output }: { output: string }) {
  return (
    <div className="p-2 rounded-lg bg-verdict-primary/10 border border-verdict-primary/20">
      <div className="flex items-center gap-1.5 text-[11px] text-verdict-primary font-mono mb-1">
        <CheckCircle2 className="w-3.5 h-3.5" />
        Source-backed Output
      </div>
      {/* Render markdown with scrollable container */}
      <div className="max-h-64 overflow-y-auto pr-1">
        <MarkdownContent content={output} className="text-xs" />
      </div>
    </div>
  );
}

/** Aggregator Loading Node - Shows while final verdict is being synthesized */
function AggregatorLoadingNode() {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const isSmallScreen = isMobile || isTablet;

  return (
    <div className={cn(
      "relative rounded-xl border-2 border-verdict-primary/50 overflow-hidden",
      "bg-gradient-to-br from-[#0D0D0D] via-[#0F0F0F] to-[#080808]",
      "animate-pulse",
      isSmallScreen ? "mx-2 mb-2 p-3" : "mx-4 mb-4 p-4"
    )}>
      {/* Pulsing border glow effect */}
      <div className="absolute inset-0 rounded-xl border-2 border-verdict-primary/30 animate-pulse" />

      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className={cn(
          "flex items-center justify-center rounded-lg",
          "bg-verdict-primary/20 text-verdict-primary",
          isSmallScreen ? "w-8 h-8" : "w-10 h-10"
        )}>
          <Scale className={cn(isSmallScreen ? "w-4 h-4" : "w-5 h-5")} />
        </div>
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <h3 className={cn(
              "font-bold text-text-primary",
              isSmallScreen ? "text-sm" : "text-base"
            )}>
              Synthesizing Final Verdict
            </h3>
            <Loader2 className={cn(
              "text-verdict-primary animate-spin",
              isSmallScreen ? "w-4 h-4" : "w-5 h-5"
            )} />
          </div>
          <p className={cn(
            "text-text-secondary",
            isSmallScreen ? "text-xs" : "text-sm"
          )}>
            Combining all research findings into a comprehensive verdict...
          </p>
        </div>
      </div>

      {/* Loading bars animation */}
      <div className="space-y-2">
        <div className="h-2 bg-surface-light rounded-full overflow-hidden">
          <div className="h-full bg-verdict-primary/40 rounded-full animate-[shimmer_2s_ease-in-out_infinite] w-3/4" />
        </div>
        <div className="h-2 bg-surface-light rounded-full overflow-hidden">
          <div className="h-full bg-verdict-primary/30 rounded-full animate-[shimmer_2s_ease-in-out_infinite_0.3s] w-1/2" />
        </div>
        <div className="h-2 bg-surface-light rounded-full overflow-hidden">
          <div className="h-full bg-verdict-primary/20 rounded-full animate-[shimmer_2s_ease-in-out_infinite_0.6s] w-5/6" />
        </div>
      </div>
    </div>
  );
}

/** Source authority type and badge info */
interface SourceBadge {
  type: 'government' | 'academic' | 'research' | 'news' | 'standard';
  label: string;
  color: string;
  bgColor: string;
}

/** Determine source authority badge based on domain */
function getSourceBadge(url: string): SourceBadge {
  try {
    const hostname = new URL(url).hostname.toLowerCase();
    // Government sources (.gov)
    if (hostname.endsWith('.gov') || hostname.endsWith('.gov.uk') || hostname.endsWith('.gov.au')) {
      return { type: 'government', label: 'GOV', color: 'text-blue-400', bgColor: 'bg-blue-400/10 border-blue-400/30' };
    }
    // Educational sources (.edu)
    if (hostname.endsWith('.edu') || hostname.endsWith('.ac.uk') || hostname.endsWith('.edu.au')) {
      return { type: 'academic', label: 'EDU', color: 'text-purple-400', bgColor: 'bg-purple-400/10 border-purple-400/30' };
    }
    // Research/Academic publishers
    const academicDomains = ['pubmed', 'ncbi.nlm.nih', 'nature.com', 'science.org', 'sciencedirect',
      'springer', 'wiley', 'arxiv.org', 'jstor.org', 'researchgate', 'scholar.google',
      'nejm.org', 'thelancet.com', 'bmj.com', 'jama', 'cell.com', 'plos.org', 'pnas.org'];
    if (academicDomains.some(d => hostname.includes(d))) {
      return { type: 'research', label: 'RESEARCH', color: 'text-emerald-400', bgColor: 'bg-emerald-400/10 border-emerald-400/30' };
    }
    // Major news/reputable sources
    const newsDomains = ['reuters.com', 'apnews.com', 'bbc.com', 'bbc.co.uk', 'npr.org',
      'nytimes.com', 'washingtonpost.com', 'wsj.com', 'economist.com', 'ft.com', 'bloomberg.com'];
    if (newsDomains.some(d => hostname.includes(d))) {
      return { type: 'news', label: 'NEWS', color: 'text-amber-400', bgColor: 'bg-amber-400/10 border-amber-400/30' };
    }
    return { type: 'standard', label: '', color: '', bgColor: '' };
  } catch {
    return { type: 'standard', label: '', color: '', bgColor: '' };
  }
}

/** Final Aggregator Node */
function AggregatorNode({
  verdict,
  sources,
  duration,
  onNewResearch,
  session,
  onFollowUp,
  hideActions,
}: {
  verdict: string;
  sources: SourceInfo[];
  duration?: number;
  onNewResearch?: () => void;
  session: ResearchSession;
  onFollowUp?: () => void;
  hideActions?: boolean;
}) {
  const [showSources, setShowSources] = useState(false);
  const [isSourcesExpanded, setIsSourcesExpanded] = useState(false);
  const displaySources = isSourcesExpanded ? sources : sources.slice(0, 5);

  // Count authoritative sources for summary
  const govSources = sources.filter(s => getSourceBadge(s.url).type === 'government').length;
  const eduSources = sources.filter(s => getSourceBadge(s.url).type === 'academic').length;
  const researchSources = sources.filter(s => getSourceBadge(s.url).type === 'research').length;
  const newsSources = sources.filter(s => getSourceBadge(s.url).type === 'news').length;

  // Responsive hooks
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const isSmallScreen = isMobile || isTablet;

  // Calculate average confidence from all task results
  const taskResults = session.reportData?.taskResults || [];
  const confidenceScores = taskResults
    .map(t => t.confidenceScore)
    .filter((c): c is number => c !== undefined);
  const avgConfidence = confidenceScores.length > 0
    ? confidenceScores.reduce((a, b) => a + b, 0) / confidenceScores.length
    : undefined;

  // Confidence level text based on score
  const getConfidenceLabel = (score: number): string => {
    if (score >= 0.85) return 'Very High';
    if (score >= 0.7) return 'High';
    if (score >= 0.5) return 'Moderate';
    return 'Low';
  };

  // Confidence color based on score
  const getConfidenceColor = (score: number): { bar: string; text: string; bg: string } => {
    if (score >= 0.7) return { bar: 'bg-emerald-500', text: 'text-emerald-400', bg: 'bg-emerald-500/10' };
    if (score >= 0.5) return { bar: 'bg-amber-500', text: 'text-amber-400', bg: 'bg-amber-500/10' };
    return { bar: 'bg-red-500', text: 'text-red-400', bg: 'bg-red-500/10' };
  };

  const getHostname = (url: string): string => {
    try {
      return new URL(url).hostname.replace('www.', '');
    } catch {
      return url;
    }
  };

  // Handle export for Analyst Pack
  const onExportReport = async (format: ExportFormat) => {
    if (!session.reportData) return;
    await handleExport(format, {
      query: session.query,
      reportData: session.reportData,
    });
  };

  return (
    <div className={cn(
      "rounded-xl border bg-verdict-primary/10 border-verdict-primary/30",
      isSmallScreen ? "p-3" : "p-4"
    )}>
      <div className={cn("flex items-center mb-3", isSmallScreen ? "gap-2" : "gap-2")}>
        <div className={cn(
          "rounded-lg bg-verdict-primary/20 flex items-center justify-center flex-shrink-0",
          isSmallScreen ? "w-8 h-8" : "w-10 h-10"
        )}>
          <Scale className={cn("text-verdict-primary", isSmallScreen ? "w-4 h-4" : "w-5 h-5")} />
        </div>
        <div className="min-w-0">
          <div className={cn(
            "font-mono uppercase text-verdict-primary/70 tracking-wider",
            isSmallScreen ? "text-[11px]" : "text-xs"
          )}>Final Verdict</div>
          <div className={cn("text-text-primary font-medium", isSmallScreen ? "text-sm" : "text-base")}>
            Research Complete
          </div>
        </div>
        {duration && (
          <div className={cn(
            "ml-auto font-mono text-text-muted flex-shrink-0",
            isSmallScreen ? "text-[11px]" : "text-xs"
          )}>
            {Math.floor(duration / 60)}m {Math.round(duration % 60)}s
          </div>
        )}
      </div>

      {/* Confidence Meter - Visual bar like S3 report */}
      {avgConfidence !== undefined && (
        <div
          className={cn(
            "rounded-lg mb-3",
            isSmallScreen ? "p-2" : "p-3",
            getConfidenceColor(avgConfidence).bg
          )}
        >
          <div className="flex items-center justify-between mb-2">
            <Tooltip content="How well sources agree. Below 70% triggers a debate to resolve conflicts." position="top">
              <span className={cn(
                "font-mono uppercase text-text-secondary cursor-help",
                isSmallScreen ? "text-[11px]" : "text-xs"
              )}>{isSmallScreen ? "Confidence" : "Confidence Level"}</span>
            </Tooltip>
            <span className={cn(
              "font-semibold",
              isSmallScreen ? "text-sm" : "text-base",
              getConfidenceColor(avgConfidence).text
            )}>
              {(avgConfidence * 100).toFixed(0)}%
            </span>
          </div>
          <div className="h-2 bg-surface-light rounded-full overflow-hidden">
            <div
              className={cn("h-full rounded-full transition-all duration-500", getConfidenceColor(avgConfidence).bar)}
              style={{ width: `${avgConfidence * 100}%` }}
            />
          </div>
          <div className={cn(
            "mt-1.5",
            isSmallScreen ? "text-[11px]" : "text-xs",
            getConfidenceColor(avgConfidence).text
          )}>
            {getConfidenceLabel(avgConfidence)}{!isSmallScreen && ` - Based on ${confidenceScores.length} task${confidenceScores.length !== 1 ? 's' : ''} analysis`}
          </div>
        </div>
      )}

      {/* Full verdict content with markdown rendering */}
      <div className="pr-2 mb-3">
        <MarkdownContent content={verdict} className={cn(isSmallScreen ? "text-xs" : "text-sm")} />
      </div>
      {/* Clickable Sources Badge */}
      {sources.length > 0 && (
        <div className="mb-3">
          <button
            onClick={() => setShowSources(!showSources)}
            className={cn(
              "flex items-center gap-1.5 text-text-muted hover:text-verdict-primary transition-colors group",
              isSmallScreen ? "text-[11px]" : "text-xs"
            )}
          >
            <Globe className={cn("opacity-70 group-hover:opacity-100", isSmallScreen ? "w-3.5 h-3.5" : "w-4 h-4")} />
            <span>{sources.length} sources</span>
            {showSources ? (
              <ChevronDown className={cn(isSmallScreen ? "w-3 h-3" : "w-3.5 h-3.5")} />
            ) : (
              <ChevronRight className={cn(isSmallScreen ? "w-3 h-3" : "w-3.5 h-3.5")} />
            )}
          </button>
          {/* Expandable Sources List */}
          {showSources && (
            <div className={cn(
              "mt-2 rounded-lg bg-surface/50 border border-border",
              isSmallScreen ? "p-2" : "p-3"
            )}>
              <div className="flex items-center justify-between mb-2">
                <span className={cn(
                  "font-mono uppercase text-text-muted",
                  isSmallScreen ? "text-[11px]" : "text-xs"
                )}>
                  Sources ({sources.length})
                </span>
                {sources.length > 5 && (
                  <button
                    onClick={() => setIsSourcesExpanded(!isSourcesExpanded)}
                    className={cn(
                      "text-text-muted hover:text-text-primary transition-colors",
                      isSmallScreen ? "text-[11px]" : "text-xs"
                    )}
                  >
                    {isSourcesExpanded ? 'Less' : `All ${sources.length}`}
                  </button>
                )}
              </div>
              {/* Source authority summary badges */}
              {(govSources > 0 || eduSources > 0 || researchSources > 0 || newsSources > 0) && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {govSources > 0 && (
                    <span className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold border bg-blue-400/10 border-blue-400/30">
                      <ShieldCheck className="w-2.5 h-2.5 text-blue-400" />
                      <span className="text-blue-400">{govSources} GOV</span>
                    </span>
                  )}
                  {eduSources > 0 && (
                    <span className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold border bg-purple-400/10 border-purple-400/30">
                      <BookOpen className="w-2.5 h-2.5 text-purple-400" />
                      <span className="text-purple-400">{eduSources} EDU</span>
                    </span>
                  )}
                  {researchSources > 0 && (
                    <span className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold border bg-emerald-400/10 border-emerald-400/30">
                      <CheckCircle2 className="w-2.5 h-2.5 text-emerald-400" />
                      <span className="text-emerald-400">{researchSources} RESEARCH</span>
                    </span>
                  )}
                  {newsSources > 0 && (
                    <span className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold border bg-amber-400/10 border-amber-400/30">
                      <Newspaper className="w-2.5 h-2.5 text-amber-400" />
                      <span className="text-amber-400">{newsSources} NEWS</span>
                    </span>
                  )}
                </div>
              )}
              <div className={cn(
                "space-y-2 overflow-y-auto",
                isSmallScreen ? "max-h-32" : "max-h-40"
              )}>
                {displaySources.map((source, idx) => {
                  const badge = getSourceBadge(source.url);
                  return (
                    <a
                      key={idx}
                      href={source.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={cn(
                        "flex flex-col gap-0.5 text-text-secondary hover:text-verdict-primary transition-colors group",
                        isSmallScreen ? "text-[11px]" : "text-xs"
                      )}
                    >
                      <div className="flex items-center gap-1.5">
                        {badge.type !== 'standard' ? (
                          <div className={cn('flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold border', badge.bgColor)}>
                            {badge.type === 'government' && <ShieldCheck className={cn('w-2.5 h-2.5', badge.color)} />}
                            {badge.type === 'academic' && <BookOpen className={cn('w-2.5 h-2.5', badge.color)} />}
                            {badge.type === 'research' && <CheckCircle2 className={cn('w-2.5 h-2.5', badge.color)} />}
                            {badge.type === 'news' && <Newspaper className={cn('w-2.5 h-2.5', badge.color)} />}
                            <span className={badge.color}>{badge.label}</span>
                          </div>
                        ) : (
                          <FileText className={cn("flex-shrink-0 opacity-50 group-hover:opacity-100", isSmallScreen ? "w-3 h-3" : "w-3.5 h-3.5")} />
                        )}
                        <span className="truncate font-medium text-text-primary group-hover:text-verdict-primary">
                          {source.title || getHostname(source.url)}
                        </span>
                      </div>
                      {!isSmallScreen && (
                        <span className="text-[11px] text-text-muted truncate ml-5">{getHostname(source.url)}</span>
                      )}
                    </a>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
      {/* Action buttons */}
      {!hideActions && (
      <div className={cn("flex flex-col", isSmallScreen ? "gap-1.5" : "gap-2")}>
        <div className={cn(
          "flex items-center",
          isSmallScreen ? "flex-col gap-1.5" : "flex-row gap-2"
        )}>
          {/* Export Menu - Analyst Pack */}
          <ExportMenu
            onExport={onExportReport}
            availableFormats={['pdf', 'csv', 'json', 'markdown']}
            reportTitle={session.query.slice(0, 50)}
          />
          {/* New Research button */}
          {onNewResearch && (
            <button
              onClick={onNewResearch}
              className={cn(
                "flex-1 rounded-lg bg-verdict-primary/20 hover:bg-verdict-primary/30 border border-verdict-primary/30 text-verdict-primary font-medium transition-colors",
                isSmallScreen ? "py-1.5 px-3 text-xs" : "py-2 px-4 text-sm"
              )}
            >
              New Research
            </button>
          )}
        </div>
        {/* Ask Follow Up Questions button - for free users (requires signup) */}
        {onFollowUp && (
          <button
            onClick={onFollowUp}
            className={cn(
              "w-full rounded-lg bg-gradient-to-r from-blue-600/20 to-purple-600/20 hover:from-blue-600/30 hover:to-purple-600/30 border border-blue-500/30 text-text-primary font-medium transition-all flex items-center justify-center gap-1.5 group",
              isSmallScreen ? "py-2 px-3 text-xs" : "py-2.5 px-4 text-sm"
            )}
            title="Sign up to unlock follow-up questions and continue your research"
          >
            <Lock className={cn(isSmallScreen ? "w-3 h-3" : "w-3.5 h-3.5", "text-text-secondary group-hover:text-verdict-primary transition-colors")} />
            <MessageSquare className={cn(isSmallScreen ? "w-3.5 h-3.5" : "w-4 h-4")} />
            <span>Follow-up Questions</span>
            <span className={cn(
              "text-text-muted font-normal",
              isSmallScreen ? "text-[10px]" : "text-xs"
            )}>(Sign up)</span>
          </button>
        )}
      </div>
      )}
    </div>
  );
}

/** Collapsed Aggregator Node - Full view of executive summary */
function CollapsedAggregatorNode({
  verdict,
  confidenceScore,
  isSmallScreen = false,
}: {
  verdict: string;
  confidenceScore?: number;
  isSmallScreen?: boolean;
}) {
  return (
    <div className={cn(
      "relative rounded-xl border backdrop-blur-md bg-gradient-to-br from-verdict-primary/15 to-amber-600/5 border-verdict-primary/40",
      isSmallScreen ? "p-3" : "p-4"
    )}>
      <div className={cn("flex items-start", isSmallScreen ? "gap-2" : "gap-3")}>
        <div className={cn(
          "rounded-lg flex items-center justify-center bg-verdict-primary/25 shadow-inner flex-shrink-0",
          isSmallScreen ? "w-8 h-8" : "w-10 h-10"
        )}>
          <Scale className={cn("text-verdict-primary", isSmallScreen ? "w-4 h-4" : "w-5 h-5")} />
        </div>
        <div className="flex-1 min-w-0">
          <div className={cn("flex items-center gap-1.5 sm:gap-2", isSmallScreen ? "mb-2" : "mb-3")}>
            <div className={cn(
              "font-mono uppercase text-verdict-primary tracking-wider font-semibold",
              isSmallScreen ? "text-xs" : "text-sm"
            )}>
              The Verdict
            </div>
            {confidenceScore !== undefined && (
              <span className={cn(
                "font-mono px-1.5 sm:px-2 py-0.5 rounded-full bg-verdict-primary/20 text-verdict-primary",
                isSmallScreen ? "text-[11px]" : "text-xs"
              )}>
                {(confidenceScore * 100).toFixed(0)}% confidence
              </span>
            )}
          </div>
          {/* Full verdict display - no truncation */}
          <div className={cn("text-text-primary", isSmallScreen ? "text-sm" : "text-base")}>
            <MarkdownContent
              content={verdict}
              className={cn(
                isSmallScreen ? "text-sm" : "text-base",
                "prose-headings:text-verdict-primary prose-headings:font-semibold prose-p:text-text-primary prose-li:text-text-primary prose-strong:text-text-primary",
                isSmallScreen ? "prose-headings:text-sm" : "prose-headings:text-base"
              )}
            />
          </div>
        </div>
      </div>
    </div>
  );
}